
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLineChat, setOpenLineUserId } from '../hooks/useLineChat';
import { useLineFilters } from '../hooks/useLineFilters';
import { playNotificationSound } from '../lib/soundUtils';
import LineChatPanel from './LineChatPanel';
import {
    Dialog, DialogContent, DialogTitle, IconButton,
    Switch, Button, Tooltip, Avatar, Box, Typography, TextField, MenuItem as MuiMenuItem, Select, FormControl, InputLabel,
    Snackbar, Alert, Popover, Menu, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChatIcon from '@mui/icons-material/Chat';
import RefreshIcon from '@mui/icons-material/Refresh';
import PushPinIcon from '@mui/icons-material/PushPin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewListIcon from '@mui/icons-material/ViewList';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { reservationSyncService } from '../services/reservationSyncService';
import { saveDispatchReservation, fetchDispatchVehicles } from '../api/dispatchService';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { toEditorReservation } from '../lib/editorDataTransform';
import ReservationEditorPanel from './editor2';

import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './LineChat.css';
import TemplateHelpDialog from './TemplateHelpDialog';
import BoardHelpDialog from './BoardHelpDialog';
import { LINE_SYNC_KEY } from '../config/instances';
import { isLineEnabled, isReservationChatEnabled, getReservationChatStoreKey } from '../config/backend';
import { parseChatDeepLink, resolveChatDeepLink, stripChatDeepLink, chatDeepLinkNotice } from '../lib/chatDeepLink';
// 自社チャット（予約者チャット）を LINE と並べる（booking-platform/md/customer-chat.md Phase 2）
import { useReservationChat } from '../hooks/useReservationChat';
import { reservationChatPusher } from '../services/reservationChatPusher';
import { listPollInterval } from '../lib/reservationChatRealtime';
import ReservationChatPanel from './ReservationChatPanel';
import { checkinUrl, formUrl } from '../lib/customerLinks';
import {
    toReservationChatInstance, selectReservationSessions, mergeChatInstances,
    isReservationInstanceKey, isSessionUnreadForStaff, isReservationSessionKey,
} from '../lib/reservationChat';
// LP 管理画面（/admin）の統合（2026-10-26・booking-platform/md/customer-chat.md §13）: 匿名チャット・種類タブ・
// 同じ予約の LINE と予約チャットのまとめ表示・LINE の紐づけ候補・チャット受付 ON/OFF・デスクトップ通知
import {
    KIND_TABS, ANON_TRIAGE_TABS, selectAnonSessions, toAnonChatInstance, filterByKindTab, countByKind,
    bundleReservationSiblings, findLineLinkCandidates, isLpChatInstance, chatKindOf, anonTriageOf, isInstanceArchived,
    type KindTab, type AnonTriage,
} from '../lib/lpChatBoard';
import { fetchChatAccepting, setChatAccepting, setOpenReservationChatSession } from '../hooks/useReservationChat';
import {
    installChatSoundUnlock, isChatSoundEnabled, setChatSoundEnabled, isChatSoundBlocked, subscribeChatSound, playChatSoundCheck,
} from '../lib/chatSound';
import { logEvent } from '../lib/eventLog';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import BuildIcon from '@mui/icons-material/Build';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import { BOARD_TOOLS, findPastReturnPins, type PastReturnPin } from '../lib/boardTools';
import UnpinPastReturnsDialog from './UnpinPastReturnsDialog';
import { getChatPushState, subscribeChatPush, unsubscribeChatPush, type ChatPushState } from '../services/chatPush';

// --- DnD Sortable Components ---
const SortableItem = ({ id, children, isSelected, onClick, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
        position: 'relative',
        padding: '12px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '8px',
        background: isSelected ? '#eff6ff' : 'white',
        border: isSelected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div ref={setNodeRef} style={style} onClick={onClick}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
                <DragIndicatorIcon fontSize="small" />
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {children}
            </div>
            {onDelete && (
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ color: '#ef4444' }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
        </div>
    );
};

const CategorySortableItem = ({ id, name, isSelected, onClick, onDelete, onEdit }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        padding: '8px 12px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '4px',
        background: isSelected ? '#eef2ff' : 'transparent',
        border: isSelected ? '1px solid #6366f1' : '1px solid transparent',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div ref={setNodeRef} style={style} onClick={onClick}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#cbd5e1' }}>
                <DragIndicatorIcon fontSize="small" />
            </div>
            <div style={{ flex: 1, fontWeight: 800, fontSize: '14px', color: isSelected ? '#4f46e5' : '#475569' }}>
                {name}
            </div>
            <Box display="flex">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                    <DeleteIcon fontSize="small" color="error" />
                </IconButton>
            </Box>
        </div>
    );
};

// --- UserAvatar Component (Simplified sync on mount) ---
const LazyAvatar = ({ user, syncProfile, sx, ...props }) => {
    const [hasAttemptedSync, setHasAttemptedSync] = useState(false);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const userName = user.custom_display_name || user.line_display_name;
        // const isTarget = userName && userName.includes('テスト太郎'); // Debugging target

        if (hasAttemptedSync) return;

        const syncWithRetry = async (currentRetry = 0) => {
            const success = await syncProfile(user.line_user_id);

            if (success) {
                setHasAttemptedSync(true);
            } else if (currentRetry < 3) {
                setTimeout(() => syncWithRetry(currentRetry + 1), 3000);
            } else {
                setHasAttemptedSync(true);
            }
        };

        // マウント時にランダムな遅延（0-1000ms）を置いて同期を開始（大量リクエスト防止）
        const delay = Math.floor(Math.random() * 1000);
        const timer = setTimeout(() => syncWithRetry(0), delay);

        return () => clearTimeout(timer);
    }, [user.line_user_id, hasAttemptedSync, syncProfile]);

    const initials = user.custom_display_name?.[0] || user.line_display_name?.[0] || '?';
    const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
    const API_KEY = LINE_SYNC_KEY;
    const PROXY_VERSION = 'v5'; // Cache busting

    const proxiedUrl = (user.line_picture_url && user.line_picture_url.startsWith('http') && !imgError)
        ? `${LINE_API_URL}?action=proxy_image&url=${encodeURIComponent(user.line_picture_url)}&api_key=${API_KEY}&v=${PROXY_VERSION}` 
        : null;

    return (
        <div className="line-avatar-container">
            <Avatar 
                src={proxiedUrl} 
                imgProps={{ crossOrigin: "anonymous" }}
                onError={(e) => {
                    // const userName = user.system_customer_name || user.line_display_name;
                    // console.warn(`[Avatar] Image failed for ${userName} (${user.line_user_id}). URL: ${proxiedUrl}`);
                    setImgError(true);
                }}
                sx={{ width: 40, height: 40, bgcolor: '#f1f5f9', color: '#64748b', ...sx }}
                {...props}
            >
                {initials}
            </Avatar>
            <div className={`link-status-badge ${user.linked_reservations?.length > 0 ? 'connected' : 'disconnected'}`}>
                {user.linked_reservations?.length > 0 ? <LinkIcon sx={{ fontSize: 'inherit' }} /> : <LinkOffIcon sx={{ fontSize: 'inherit' }} />}
            </div>
        </div>
    );
};

const getAlertColor = (level: number): string | null => {
    switch (level) {
        case 1: return '#2563eb'; // Blue
        case 2: return '#f59e0b'; // Yellow
        case 3: return '#ef4444'; // Red
        default: return null;
    }
};

const getAlertTooltip = (level: number): string => {
    switch (level) {
        case 1: return 'AI保留中（ルールによりホールド）';
        case 2: return '料金・スケジュール変更の相談あり';
        case 3: return '緊急：空港到着・配車対応が必要';
        default: return '';
    }
};

const getAlertLabel = (level: number): string => {
    switch (level) {
        case 1: return 'AI保留';
        case 2: return '相談あり';
        case 3: return '緊急';
        default: return '';
    }
};

const BOARD_LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
const BOARD_API_KEY = LINE_SYNC_KEY;

const LineChatBoard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        users, tags, optimisticMessages, fetchUsers, loading, togglePin, toggleArchive, sendBroadcast, syncProfile,
        fetchTemplates, saveTemplate, deleteTemplate,
        fetchTemplateCategories, saveTemplateCategory, deleteTemplateCategory,
        updateCategoryOrder, updateTemplateOrder, fetchTags, linkReservation,
        pusherConnected
    } = useLineChat();
    const { reservationStatusList, vehicleTypeList } = useReservationStatuses();
    // 自社チャット（予約者チャット）。LINE が無い宮古でもこちらは有効
    const lineEnabled = isLineEnabled();
    const resvChat = useReservationChat();
    const resvChatEnabled = isReservationChatEnabled();
    // 種類タブ（すべて / LINE / 予約チャット / 匿名チャット / アーカイブ）と匿名の区分。端末に保存
    // 「アーカイブ」タブのときだけアーカイブを読み込む（旧: 右上メニューの「アーカイブ表示」トグル。2026-10-26 §13-5 で置き換え）。
    // chatInstances の計算より前に宣言する
    const [kindTab, setKindTabState] = useState<KindTab>(() => {
        const v = localStorage.getItem('line_kind_tab');
        return (KIND_TABS.some(t => t.value === v) ? v : 'all') as KindTab;
    });
    const [anonTriage, setAnonTriageState] = useState<AnonTriage>(() => {
        const v = localStorage.getItem('line_anon_triage');
        return (ANON_TRIAGE_TABS.some(t => t.value === v) ? v : 'attention') as AnonTriage;
    });
    const setKindTab = (v: KindTab) => { setKindTabState(v); localStorage.setItem('line_kind_tab', v); };
    const setAnonTriage = (v: AnonTriage) => { setAnonTriageState(v); localStorage.setItem('line_anon_triage', v); };
    const showArchived = kindTab === 'archive';
    
    // Reservation Editor State
    const [selectedResForEditor, setSelectedResForEditor] = useState(null);
    const [editingResForEditor, setEditingResForEditor] = useState(null);
    const [vehiclesCache, setVehiclesCache] = useState({});
    const [isSavingRes, setIsSavingRes] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // 検索: サーバー側でしかマッチできない（メッセージ本文全文・アーカイブ等の）
    // ユーザーIDと、その実体（ベースの全件リストに含まれないアーカイブユーザー等）。
    // クライアント側の customer_name 検索と「ユニオン」して取りこぼしを防ぐ。
    const [serverMatchedUids, setServerMatchedUids] = useState<Set<string>>(new Set());
    const [searchExtraUsers, setSearchExtraUsers] = useState<any[]>([]);

    // ベースの全件リスト(users)に、サーバー検索の補完ユーザーをマージ（uidで重複排除）
    const boardUsers = React.useMemo(() => {
        if (!searchExtraUsers || searchExtraUsers.length === 0) return users;
        const map = new Map<string, any>();
        users.forEach((u: any) => map.set(u.line_user_id, u));
        searchExtraUsers.forEach((u: any) => {
            if (!map.has(u.line_user_id)) map.set(u.line_user_id, u);
        });
        return Array.from(map.values());
    }, [users, searchExtraUsers]);

    // reservationSyncServiceの変更を購読して予約データ更新時にchatInstancesを再計算
    useEffect(() => {
        const unsub = reservationSyncService.subscribe(() => {
            setRefreshTrigger(prev => prev + 1);
        });
        return () => { if (typeof unsub === 'function') unsub(); };
    }, []);

    const handleStaffChatRegistration = (instance) => {
        if (isLpChatInstance(instance)) return; // スタッフチャット登録は LINE ユーザーのみ（自社チャットには LINE ID が無い）
        setStaffRegTarget(instance);
        setStaffRegName('');
    };

    const confirmStaffRegistration = async () => {
        if (!staffRegTarget || !staffRegName.trim()) return;
        const success = await linkReservation(staffRegTarget.line_user_id, `STAFF:${staffRegName.trim()}`);
        setStaffRegTarget(null);
        if (success) {
            showMessage('スタッフチャットとして登録しました');
            fetchUsers();
        } else {
            showMessage('登録に失敗しました', 'error');
        }
    };

    // NEW: Expand users (X) to chat instances (Y)
    const chatInstances = React.useMemo(() => {
        const instances = [];
        const cachedReservations = reservationSyncService.getReservations();
        
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.getFullYear() + '-' + String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + String(tomorrow.getDate()).padStart(2, '0');

        boardUsers.forEach(user => {
            const resIds = user.reservation_ids || [];
            if (resIds.length === 0) {
                // 紐付けなしユーザーはそのまま表示
                instances.push({
                    ...user,
                    instance_reservation_id: null,
                    instanceKey: `${user.line_user_id}_none`,
                    hasOtherReservations: false,
                    reservation_statuses: []
                });
            } else {
                // すべての紐付けがSTAFFチャットの場合は、リスト自体に表示させない
                const hasRealReservations = resIds.some(rid => !String(rid).startsWith('STAFF:'));
                if (!hasRealReservations && resIds.length > 0) return;

                resIds.forEach(rid => {
                    // STAFFチャット（"STAFF:"から始まるID）はボードには表示しない
                    if (String(rid).startsWith('STAFF:')) return;

                    // reservationSyncServiceのみから予約データ取得（DBキャッシュ不使用）
                    const resData = cachedReservations.find(r => String(r.id) === String(rid)) || null;

                    // バッジを最新データで計算
                    const newBadges: string[] = [];
                    if (resData) {
                        const status = resData.status || resData.status_code;
                        const pDate = (resData.start_date || '').split(' ')[0].split('T')[0];
                        const rDate = (resData.end_date || '').split(' ')[0].split('T')[0];

                        if (status === 'in_use' || status === 'in-use' || status === '利用中') newBadges.push('利用中');
                        if (pDate === todayStr) newBadges.push('本日貸出');
                        if (rDate === todayStr) newBadges.push('本日返却');
                        if (pDate === tomorrowStr) newBadges.push('明日貸出');
                        if (rDate === tomorrowStr) newBadges.push('明日返却');
                    }

                    instances.push({
                        ...user,
                        instance_reservation_id: rid,
                        instance_res_data: resData,
                        instanceKey: `${user.line_user_id}_${rid}`,
                        hasOtherReservations: resIds.length > 1,
                        reservation_statuses: newBadges
                    });
                });
            }
        });

        // 自社チャット（予約者チャット `res_…`）を同じ形にして混ぜる。
        // 予約データは LINE と同じく reservationSyncService からのみ（LP の get_sessions が返すのは
        // reservation_id / contact_code / resv_customer_name だけ。無い間は memo「予約 {連絡コード} {氏名}」で補う）
        const resvInstances = selectReservationSessions(resvChat.sessions, showArchived).map(s =>
            toReservationChatInstance(
                s,
                (id) => cachedReservations.find(r => String(r.id) === String(id)),
                todayStr,
                tomorrowStr,
            )
        );
        // 匿名チャット（LP を訪れたお客様の予約前の質問など。/admin から統合）
        const anonInstances = selectAnonSessions(resvChat.sessions, showArchived, Date.now()).map(toAnonChatInstance);
        return mergeChatInstances(instances, [...resvInstances, ...anonInstances]);
    }, [boardUsers, refreshTrigger, resvChat.sessions, showArchived]);

    // Tag filter state (must be before useLineFilters)
    const [selectedTagFilters, setSelectedTagFilters] = useState([]);
    const [tagFilterAnchor, setTagFilterAnchor] = useState(null);

    const {
        filters, toggleFilter, searchQuery, setSearchQuery,
        filteredUsers: filteredInstances,
        customDateFilter, setCustomDateFilter
    } = useLineFilters(chatInstances, JSON.parse(localStorage.getItem('line_filters') || '[]'), selectedTagFilters, serverMatchedUids);

    const kindCounts = React.useMemo(() => countByKind(filteredInstances), [filteredInstances]);
    // 表示する一覧: 種類タブで絞り、「すべて」では同じ予約の LINE と予約チャットを 1 枚にまとめる
    const boardInstances = React.useMemo(() => {
        const byTab = filterByKindTab(filteredInstances, kindTab, anonTriage);
        return kindTab === 'all' ? bundleReservationSiblings(byTab) : byTab;
    }, [filteredInstances, kindTab, anonTriage]);

    // チャット受付 ON/OFF（LP の chat_system_status。cron の営業時間切替とは別に手で切り替えられる）
    const [chatAccepting, setChatAcceptingState] = useState<boolean | null>(null);
    const [chatAcceptingBusy, setChatAcceptingBusy] = useState(false);
    // デスクトップ通知（LP の Web Push をこの端末で購読）
    const [pushState, setPushState] = useState<ChatPushState>('unsupported');
    const [pushBusy, setPushBusy] = useState(false);

    // Update selection to use instanceKey
    const [selectedInstanceKey, setSelectedInstanceKey] = useState(null);
    // 同じ予約の LINE と予約チャットを開いたときの表示（null = 開いたカードの種類）。カードを替えたら戻す
    const [pairView, setPairView] = useState<'line' | 'resv' | null>(null);
    useEffect(() => { setPairView(null); }, [selectedInstanceKey]);
    // 開いている自社チャットの会話は受信音を鳴らさない（LINE は setOpenLineUserId で同じことをしている）
    useEffect(() => {
        setOpenReservationChatSession(selectedInstanceKey && isReservationInstanceKey(selectedInstanceKey) ? selectedInstanceKey.slice('resv:'.length) : null);
        return () => setOpenReservationChatSession(null);
    }, [selectedInstanceKey]);

    // 受信音（/admin の通知音を移した・2026-10-26 §13-5）: ON/OFF・自動再生の制限の案内
    const [soundOn, setSoundOn] = useState<boolean>(() => isChatSoundEnabled());
    const [soundBlocked, setSoundBlocked] = useState<boolean>(() => isChatSoundBlocked());
    useEffect(() => {
        installChatSoundUnlock();
        setSoundBlocked(isChatSoundBlocked());
        return subscribeChatSound(() => { setSoundOn(isChatSoundEnabled()); setSoundBlocked(isChatSoundBlocked()); });
    }, []);
    const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('line_auto_refresh') === 'on');
    const [readMode, setReadMode] = useState(() => localStorage.getItem('line_read_mode') === 'on');
    const [aiReplyMode, setAiReplyModeState] = useState<number>(0);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('line_view_mode') || 'grid');
    const [showHelp, setShowHelp] = useState(false);
    const [showBoardHelp, setShowBoardHelp] = useState(false);
    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastText, setBroadcastText] = useState('');
    const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
    const [broadcastStep, setBroadcastStep] = useState<'compose' | 'preview'>('compose');
    const [preparedMessages, setPreparedMessages] = useState<{ uid: string; text: string; name: string; instanceKey: string }[]>([]);
    const [previewPage, setPreviewPage] = useState(0);
    const [visibleCount, setVisibleCount] = useState(50);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const [staffRegTarget, setStaffRegTarget] = useState(null);
    const [staffRegName, setStaffRegName] = useState('');
    const [searchIncludedArchived, setSearchIncludedArchived] = useState(false);
    const [archiveToasts, setArchiveToasts] = useState([]);

    // 自社チャットの新着検知（ポーリングのみなので、未読になった会話が増えたら通知音）
    const prevUnreadResvRef = useRef<Set<string> | null>(null);
    useEffect(() => {
        // 匿名チャットは要対応だけ鳴らす（自動応答のみ・ジャンクで鳴らさない。/admin の要対応と同じ考え方）
        const now = new Set(resvChat.sessions
            .filter(s => isSessionUnreadForStaff(s) && (isReservationSessionKey(s.session_key) || anonTriageOf(s) === 'attention'))
            .map(s => s.session_key));
        const prev = prevUnreadResvRef.current;
        prevUnreadResvRef.current = now;
        if (!prev) return; // 初回は鳴らさない
        for (const k of now) {
            if (!prev.has(k) && `resv:${k}` !== selectedInstanceKey) { playNotificationSound(); break; }
        }
    }, [resvChat.sessions, selectedInstanceKey]);

    // 継続的なユーザー更新と新着検知
    const lastUsersRef = useRef(users);
    
    useEffect(() => {
        // モーダル表示中や一括選択中、検索中などは背景の自動更新を一時停止して安定性を高める
        if (!autoRefresh || searchQuery || multiSelectMode || selectedInstanceKey) return;

        // Pusher接続中は60秒（フォールバック）、切断時は15秒
        const pollInterval = pusherConnected ? 60000 : 15000;

        const interval = setInterval(async () => {
            // 自社チャットは LP の Pusher 接続中なら専用の 60 秒ポーリングに任せる（新着検知は sessions の effect）
            if (resvChatEnabled && !resvChat.realtimeConnected) resvChat.fetchSessions();
            const updatedUsers = await fetchUsers('', showArchived);

            if (updatedUsers && Array.isArray(updatedUsers)) {
                // Pusher未接続時のみポーリングで新着検知＋通知音
                if (!pusherConnected) {
                    const hasNewMessage = updatedUsers.some(newUser => {
                        const oldUser = lastUsersRef.current.find(u => u.line_user_id === newUser.line_user_id);
                        if (!oldUser) return newUser.unread_count > 0;
                        return (newUser.unread_count > oldUser.unread_count) ||
                               (newUser.last_message_at && newUser.last_message_at > oldUser.last_message_at);
                    });
                    if (hasNewMessage) {
                        playNotificationSound();
                    }
                }
                lastUsersRef.current = updatedUsers;
            }
        }, pollInterval);

        return () => clearInterval(interval);
    }, [autoRefresh, searchQuery, fetchUsers, showArchived, pusherConnected, resvChatEnabled, resvChat.fetchSessions, resvChat.realtimeConnected]);

    // usersが外部から更新された場合もRefを同期
    useEffect(() => {
        lastUsersRef.current = users;
    }, [users]);

    // Pusher: archived user got a new message — re-fetch to bring them into the list
    useEffect(() => {
        const handler = () => fetchUsers('', showArchived);
        window.addEventListener('line-pusher-refetch', handler);
        return () => window.removeEventListener('line-pusher-refetch', handler);
    }, [fetchUsers, showArchived]);

    // 自動アーカイブはサーバーの日次処理（sys/line/archive_sweep.php）に一本化した（2026-10-10）。
    // 旧: ここでランディング時に「3日以上会話なし＋紐付けなし」をアーカイブしていたが、紐付けのある顧客は
    // 二度とアーカイブされず、画面が持つ予約データの範囲でしか判定できなかった。規則は sys/line/archive_rules.php

    // Archive toast auto-dismiss (3 seconds)
    useEffect(() => {
        if (archiveToasts.length === 0) return;
        const timer = setTimeout(() => {
            setArchiveToasts(prev => prev.slice(0, -1));
        }, 3000);
        return () => clearTimeout(timer);
    }, [archiveToasts]);

    // Persistence
    useEffect(() => {
        localStorage.setItem('line_auto_refresh', autoRefresh ? 'on' : 'off');
    }, [autoRefresh]);

    useEffect(() => {
        localStorage.setItem('line_view_mode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem('line_read_mode', readMode ? 'on' : 'off');
    }, [readMode]);

    // AI返信モード初期化
    useEffect(() => {
        if (!lineEnabled) return; // 宮古: LINE API（那覇 DB）を叩かない
        fetch(`${BOARD_LINE_API_URL}?action=get_ai_reply_mode&api_key=${BOARD_API_KEY}`)
            .then(r => r.json())
            .then(d => { if (d.success) setAiReplyModeState(d.mode); })
            .catch(() => {});
    }, [lineEnabled]);

    const handleAiReplyModeChange = useCallback((mode: number) => {
        if (!lineEnabled) return;
        setAiReplyModeState(mode);
        fetch(`${BOARD_LINE_API_URL}?action=set_ai_reply_mode&api_key=${BOARD_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mode }),
        }).catch(() => {});
    }, [lineEnabled]);

    useEffect(() => {
        if (filters.length > 0) {
            localStorage.setItem('line_filters', JSON.stringify(filters));
        } else {
            localStorage.removeItem('line_filters');
        }
    }, [filters]);

    // Apply persisted filters on mount
    useEffect(() => {
        const savedFilters = localStorage.getItem('line_filters');
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                if (Array.isArray(parsed)) {
                    // We need to set them via toggleFilter or directly if we have access to setter
                    // Since useLineFilters only returns toggleFilter, we might need to update the hook
                    // Or just use the setter if we exposure it.
                    // For now, let's just use toggleFilter repeatedly or update the hook.
                    parsed.forEach(f => {
                        if (!filters.includes(f)) toggleFilter(f);
                    });
                }
            } catch (e) {
                console.error('Failed to parse saved filters', e);
            }
        }
    }, []);
    
    
    const [showTemplateManager, setShowTemplateManager] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editTemplate, setEditTemplate] = useState({ name: '', content: '', category_id: null });
    
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editCategory, setEditCategory] = useState({ id: null, name: '' });
    const [selectedCategoryId, setSelectedCategoryId] = useState(null); // Filter for template list

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const showMessage = (message, severity = 'success') => setNotification({ open: true, message, severity });
    const handleCloseNotification = () => setNotification(prev => ({ ...prev, open: false }));

    const [dateAnchor, setDateAnchor] = useState(null);
    const [tempDate, setTempDate] = useState(new Date().toISOString().split('T')[0]);

    const handleSelectAll = () => {
        // 一斉送信（LINE 公式アカウント）の対象は LINE のインスタンスだけ。自社チャットは除く
        setSelectedUserIds(boardInstances.filter(i => !isReservationInstanceKey(i.instanceKey)).map(i => i.instanceKey));
    };
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const loadTemplatesData = useCallback(async () => {
        const [t, c] = await Promise.all([
            fetchTemplates(),
            fetchTemplateCategories()
        ]);
        setTemplates(t);
        setCategories(c);
    }, [fetchTemplates, fetchTemplateCategories]);

    useEffect(() => {
        if (showTemplateManager) {
            loadTemplatesData();
        }
    }, [showTemplateManager, loadTemplatesData]);

    useEffect(() => {
        // 初期状態（検索なし）では通常通り取得
        if (!searchQuery) {
            fetchUsers('', showArchived);
        }
    }, [fetchUsers, searchQuery, showArchived]);

    // 自社チャットの一覧: 初回取得＋ポーリング（LINE の「定期読み込み」トグルや LINE の Pusher とは独立）。
    // LP の Pusher（reservationChatPusher・private-admin-chat）に繋がっている間は 60 秒の保険、未接続は 30 秒
    useEffect(() => {
        if (!resvChatEnabled) return;
        reservationChatPusher.connect();
        resvChat.fetchSessions();
        const timer = setInterval(() => { resvChat.fetchSessions(); }, listPollInterval(resvChat.realtimeConnected));
        return () => clearInterval(timer);
    }, [resvChatEnabled, resvChat.fetchSessions, resvChat.realtimeConnected]);

    // チャット受付の状態（LP）とデスクトップ通知の状態（この端末）。受付は cron の営業時間切替もあるので 60 秒ごとに読み直す
    useEffect(() => {
        if (!resvChatEnabled) return;
        let alive = true;
        const load = () => fetchChatAccepting().then(v => { if (alive && v !== null) setChatAcceptingState(v); });
        load();
        getChatPushState().then(v => { if (alive) setPushState(v); });
        const timer = setInterval(load, 60000);
        return () => { alive = false; clearInterval(timer); };
    }, [resvChatEnabled]);

    const handleToggleChatAccepting = async () => {
        if (chatAccepting === null || chatAcceptingBusy) return;
        const next = !chatAccepting;
        if (!next && !window.confirm('自社チャット（LP）の受付を OFF にしますか？\nOFF の間、LP のチャットは AI が一次対応します（営業時間の自動切替は cron で別に動きます）。')) return;
        setChatAcceptingBusy(true);
        const r = await setChatAccepting(next);
        setChatAcceptingBusy(false);
        if (r === null) { showMessage('チャット受付を切り替えられませんでした', 'error'); return; }
        setChatAcceptingState(r);
        showMessage(r ? '自社チャットの受付を ON にしました' : '自社チャットの受付を OFF にしました');
    };

    const handleTogglePush = async () => {
        if (pushBusy) return;
        setPushBusy(true);
        const err = pushState === 'on' ? await unsubscribeChatPush() : await subscribeChatPush();
        setPushBusy(false);
        setPushState(await getChatPushState());
        if (err) showMessage(err, 'error');
        else showMessage(pushState === 'on' ? 'デスクトップ通知を OFF にしました' : 'デスクトップ通知を ON にしました（タブを閉じても届きます）');
    };

    // 検索: ベースの全件リスト(users)はクライアント側で customer_name / 連絡コード /
    // 予約番号 等を含めて即時フィルタする（useLineFilters）。
    // ここでは、サーバーにしかない情報（メッセージ本文の全文・アーカイブユーザー）での
    // マッチを「補完」するため、デバウンスして非破壊のサーバー検索を行い、
    // ヒットしたユーザーIDと実体を serverMatchedUids / searchExtraUsers に反映する。
    // → ベースリストを壊さないので、サーバーが拾えない customer_name 一致が
    //   サーバー結果で上書き消去される（＝検索しても出ない）問題が起きない。
    useEffect(() => {
        if (!searchQuery) {
            setSearchIncludedArchived(false);
            setServerMatchedUids(new Set());
            setSearchExtraUsers([]);
            return;
        }
        // 新しいクエリ入力時は前回のサーバーマッチを即時クリア（stale 結果の混入防止）
        setServerMatchedUids(new Set());
        setSearchExtraUsers([]);

        const timer = setTimeout(async () => {
            setSearchIncludedArchived(false);
            // 1段階目: showArchived に合わせて非破壊検索
            let results = (await fetchUsers(searchQuery, showArchived, false)) || [];
            // 2段階目: 0件 && アーカイブ未表示 → アーカイブ含めて再検索
            if (results.length === 0 && !showArchived) {
                const deepResults = (await fetchUsers(searchQuery, true, false)) || [];
                if (deepResults.length > 0) {
                    results = deepResults;
                    setSearchIncludedArchived(true);
                }
            }
            setServerMatchedUids(new Set(results.map((u: any) => u.line_user_id)));
            setSearchExtraUsers(results);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery, fetchUsers, showArchived]);

    // Load vehicles for editor
    useEffect(() => {
        const loadVehicles = async () => {
            try {
                const data = await fetchDispatchVehicles();
                if (Array.isArray(data)) {
                    const vMap = {};
                    data.forEach(v => { vMap[String(v.id)] = v; });
                    setVehiclesCache(vMap);
                }
            } catch (err) {
                console.error('Failed to load vehicles for editor', err);
            }
        };
        loadVehicles();
    }, []);

    // Sync editing residue when selectedRes changes
    // LINE の instance_res_data は不完全な場合があるので、キャッシュから完全データを補完
    // アニメーションはReservationEditor内部で制御（ID変化を検知して閉じ→開き）
    useEffect(() => {
        if (selectedResForEditor) {
            const id = String(selectedResForEditor.id || selectedResForEditor.reservation_id || '');
            const cached = id ? reservationSyncService.cachedReservations?.get(id) : null;
            const fullData = cached ? { ...cached, ...selectedResForEditor } : selectedResForEditor;
            setEditingResForEditor(toEditorReservation(fullData));
        } else {
            setEditingResForEditor(null);
        }
    }, [selectedResForEditor]);

    const handleSaveReservation = async (updated: any, options: any = {}) => {
        const { keepOpen = false } = options;
        const dataToSave = updated || editingResForEditor;
        if (isSavingRes) {
            console.warn('[LineChatBoard] Already saving, skipping duplicate request');
            return;
        }

        setIsSavingRes(true);
        try {
            const result = await saveDispatchReservation(dataToSave);
            showMessage('予約を更新しました');
            
            // 重要: キャッシュを強制更新
            if (result && result.id) {
                reservationSyncService.updateSingleReservation(result);
                // 再計算を強制するためにトリガーを更新
                setRefreshTrigger(prev => prev + 1);
            }

            // 先にパネルを閉じて、その後にリフレッシュ処理を行う
            if (!keepOpen) {
                setSelectedResForEditor(null);
            }

            // 非同期でユーザー情報を更新（これによってリストが再描画される）
            fetchUsers(searchQuery, true); 
        } catch (err) {
            console.error('[LineChatBoard] Failed to save reservation:', err);
            showMessage('保存に失敗しました: ' + (err.message || ''), 'error');
        } finally {
            // ステートのリセット（わずかに遅延させて、後続の副作用との競合を避ける）
            setTimeout(() => {
                setIsSavingRes(false);
            }, 0);
        }
    };

    // 無限スクロール用：スクロール位置を監視して表示件数を増やす
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
                setVisibleCount(prev => {
                    if (prev < boardInstances.length) {
                        return prev + 30;
                    }
                    return prev;
                });
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [boardInstances.length]);

    // フィルターや検索条件が変わった時は表示件数をリセット
    useEffect(() => {
        setVisibleCount(50);
    }, [filters, searchQuery, customDateFilter, kindTab, anonTriage]);
    
    /* プロフィール同期は LazyAvatar (IntersectionObserver) により表示時に自動で行われるため、ここでの一斉同期は不要 */
    
    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const handleTemplateDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const visibleTemplates = templates.filter(t => 
            selectedCategoryId === null ? true : 
            selectedCategoryId === 'none' ? t.category_id === null : 
            String(t.category_id) === String(selectedCategoryId)
        ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        const oldIndex = visibleTemplates.findIndex(t => String(t.id) === String(active.id));
        const newIndex = visibleTemplates.findIndex(t => String(t.id) === String(over.id));
        
        if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedVisible = arrayMove(visibleTemplates, oldIndex, newIndex);
            
            // 1. Update master templates list WITH NEW ARRAY ORDER
            const newTemplates = [...templates];
            
            // We need to actually move the items in the master list to match the visual order
            // or at least update all sort_order properties correctly.
            reorderedVisible.forEach((t, idx) => {
                const masterIdx = newTemplates.findIndex(mt => String(mt.id) === String(t.id));
                if (masterIdx !== -1) {
                    newTemplates[masterIdx] = { ...t, sort_order: idx };
                }
            });
            
            setTemplates(newTemplates);

            const orders = reorderedVisible.map((t, idx) => ({
                id: String(t.id),
                sort_order: idx,
                category_id: t.category_id
            }));
            await updateTemplateOrder(orders);
        }
    };

    const handleCategoryDragEnd = async (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = categories.findIndex(c => String(c.id) === String(active.id));
            const newIndex = categories.findIndex(c => String(c.id) === String(over.id));
            
            const newCategories = arrayMove(categories, oldIndex, newIndex);
            setCategories(newCategories);

            const orders = newCategories.map((c, idx) => ({
                id: c.id,
                sort_order: idx
            }));
            await updateCategoryOrder(orders);
        }
    };

    const handleSaveCategory = async () => {
        if (!editCategory.name.trim()) return;
        const success = await saveTemplateCategory(editCategory);
        if (success) {
            showMessage('カテゴリを保存しました');
            setShowCategoryForm(false);
            setEditCategory({ id: null, name: '' });
            loadTemplatesData();
        } else {
            showMessage('カテゴリの保存に失敗しました', 'error');
        }
    };

    const handleDeleteCategory = async (id) => {
        if (window.confirm('このカテゴリを削除しますか？（属するテンプレートは未分類になります）')) {
            const success = await deleteTemplateCategory(id);
            if (success) {
                showMessage('カテゴリを削除しました');
                loadTemplatesData();
            } else {
                showMessage('カテゴリの削除に失敗しました', 'error');
            }
        }
    };

    const handleRefresh = useCallback(() => {
        fetchUsers(searchQuery || '', showArchived);
        if (resvChatEnabled) resvChat.fetchSessions();
    }, [fetchUsers, searchQuery, showArchived, resvChatEnabled, resvChat.fetchSessions]);

    const handleUserSelect = (instanceKey) => {
        setSelectedInstanceKey(instanceKey);
        // 通知音制御: 今開いているユーザーIDを共有シングルトンに伝える（自社チャットは LINE ではないので null）
        const lineUserId = instanceKey && !isReservationInstanceKey(instanceKey) ? instanceKey.split('_')[0] : null;
        setOpenLineUserId(lineUserId);
        if (isMobile) setMobileMenuAnchor(null);
    };

    // URL で会話を開く（未返信チャットの通知メールのリンク `/line?chat=<session_key>&store=…`・2026-10-31 §13-8）。
    // 一覧の読み込みが終わるまで待ち、見つかればカードを手で押したのと同じ handleUserSelect で開く（既読も手で開いたときと同じ）。
    // 見つからない・他店舗なら一覧のまま短い案内を出す。どちらでもパラメータは URL から消す（再読み込みで開き直さない）
    const deepLinkRef = useRef(typeof window !== 'undefined' ? parseChatDeepLink(window.location.search) : null);
    const lineFetchSeenRef = useRef(false);
    if (loading) lineFetchSeenRef.current = true;
    useEffect(() => {
        const link = deepLinkRef.current;
        if (!link) return;
        const lineReady = !lineEnabled || users.length > 0 || (lineFetchSeenRef.current && !loading);
        const lpReady = !resvChatEnabled || resvChat.loaded;
        const needLp = link.target?.kind === 'lp';
        const needLine = link.target?.kind === 'line';
        if ((needLp && !lpReady) || (needLine && !lineReady)) return;
        deepLinkRef.current = null;
        const result = resolveChatDeepLink(link, {
            ownStore: getReservationChatStoreKey(),
            lpEnabled: resvChatEnabled,
            lineEnabled,
            sessions: resvChat.sessions,
            instances: chatInstances,
        });
        try {
            window.history.replaceState(window.history.state, '', window.location.pathname + stripChatDeepLink(window.location.search) + window.location.hash);
        } catch (e) {
            logEvent({ action: 'line_board.deep_link_strip', result: 'fail', err: e });
        }
        if (result.status === 'open') {
            handleUserSelect(result.instanceKey);
            logEvent({ action: 'line_board.deep_link', target: link.target?.kind, result: 'ok', detail: { status: 'open' } });
        } else {
            setNotification({ open: true, message: chatDeepLinkNotice(result, link) || '', severity: 'info' });
            logEvent({ action: 'line_board.deep_link', target: link.target?.kind || 'invalid', result: 'ok', detail: { status: result.status } });
        }
    }, [loading, users, resvChat.loaded, resvChat.sessions, chatInstances]);

    const toggleUserSelection = (instanceKey) => {
        if (isReservationInstanceKey(instanceKey)) return; // 一斉送信は LINE のみ
        setSelectedUserIds(prev =>
            prev.includes(instanceKey) ? prev.filter(id => id !== instanceKey) : [...prev, instanceKey]
        );
    };

    // 変数置換ヘルパー
    const formatBroadcastDateTime = (dtStr) => {
        if (!dtStr) return '';
        const d = new Date(dtStr);
        return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const formatBroadcastTime = (dtStr) => {
        if (!dtStr) return '';
        const d = new Date(dtStr);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    // 変数置換してプレビュー用メッセージを生成
    const generatePreparedMessages = () => {
        return selectedUserIds.map(instanceKey => {
            const instance = chatInstances.find(i => i.instanceKey === instanceKey);
            const uid = instanceKey.split('_')[0];
            let replacedText = broadcastText;
            let customerName = 'お客様';

            if (instance) {
                const res = instance.instance_res_data;
                customerName = res?.customer_name || instance.custom_display_name || instance.line_display_name || 'お客様';

                const vars = {
                    name: customerName,
                    customer_name: customerName,
                    id: res?.id || '',
                    reservation_id: res?.id || '',
                    start_date: formatBroadcastDateTime(res?.start_date),
                    end_date: formatBroadcastDateTime(res?.end_date),
                    start_date_time: formatBroadcastTime(res?.start_date),
                    end_date_time: formatBroadcastTime(res?.end_date),
                    start_time: formatBroadcastTime(res?.start_date),
                    end_time: formatBroadcastTime(res?.end_date),
                    status: res?.status_name || '',
                    contact_code: res?.contact_code || '',
                    // お客様向けリンクは予約番号で作る（連番 ID の ?id= はページ側で受け付けない・2026-10-12）
                    form: res?.contact_code ? formUrl('https://n-car.jp', res.contact_code) : '',
                    checkin: res?.contact_code ? checkinUrl('https://n-car.jp', res.contact_code) : ''
                };

                Object.keys(vars).forEach(key => {
                    const regex = new RegExp(`\\{\\s*${key}\\s*\\}`, 'gi');
                    replacedText = replacedText.replace(regex, vars[key]);
                });
            }

            return { uid, text: replacedText, name: customerName, instanceKey };
        });
    };

    // プレビューを表示
    const handleShowPreview = () => {
        if (!broadcastText.trim() || selectedUserIds.length === 0) return;
        const msgs = generatePreparedMessages();
        setPreparedMessages(msgs);
        setPreviewPage(0);
        setBroadcastStep('preview');
    };

    // プレビュー画面から送信
    const handleSendBroadcast = async () => {
        if (preparedMessages.length === 0) return;

        setIsSendingBroadcast(true);
        const apiMessages = preparedMessages.map(m => ({ uid: m.uid, text: m.text }));
        const results = await sendBroadcast(selectedUserIds, broadcastText, apiMessages);
        setIsSendingBroadcast(false);

        if (results && (results.success_count !== undefined)) {
            setNotification({
                open: true,
                message: `${results.success_count}件の送信に成功しました。${results.failed_count}件失敗。`,
                severity: results.failed_count > 0 ? 'warning' : 'success'
            });

            if (results.failed_count > 0) {
                console.error('[DEBUG-BULK] Some messages failed to send:', results.errors);
            }

            setShowBroadcastModal(false);
            setBroadcastStep('compose');
            setMultiSelectMode(false);
            setSelectedUserIds([]);
            setBroadcastText('');
            setPreparedMessages([]);
        } else {
            console.error('[DEBUG-BULK] Unexpected broadcast response:', results);
            setNotification({
                open: true,
                message: '送信プロセスの実行中に予期しないエラーが発生しました。',
                severity: 'error'
            });
        }
    };

    /**
     * アーカイブ（一覧から外す）／戻す（2026-10-26 §13-5）。お客様の画面は変わらない。
     * - LINE: sys/line toggle_archive（そのユーザーの会話全体）
     * - 予約チャット・匿名チャット: LP staff_archive。対応完了（status=ARCHIVED）を戻すときは unarchive_session（お客様のチャットも再開）
     * - LINE と予約チャットをまとめたカード: 両方
     */
    const handleArchiveInstance = async (instance, archive: boolean) => {
        const name = instance.custom_display_name || instance.line_display_name || '会話';
        if (!archive && instance.chat_closed
            && !window.confirm(`${name} は「対応完了」で終了した会話です。戻すと、お客様のチャットも再開します。戻しますか？`)) return;
        const jobs: Promise<boolean>[] = [];
        if (isLpChatInstance(instance)) {
            if (archive || instance.staff_archived) jobs.push(resvChat.setStaffArchived([instance.session_key], archive));
            if (!archive && instance.chat_closed) jobs.push(resvChat.setArchived(instance.session_key, false));
        } else {
            jobs.push(toggleArchive(instance.line_user_id, archive));
            const sib = instance.sibling_resv;
            if (sib) jobs.push(resvChat.setStaffArchived([sib.session_key], archive));
        }
        const results = await Promise.all(jobs);
        const ok = results.every(Boolean);
        logEvent({ action: archive ? 'line_board.archive' : 'line_board.unarchive', target: instance.instanceKey, result: ok ? 'ok' : 'fail', detail: { kind: chatKindOf(instance), parts: results.length, bundled: Boolean(instance.sibling_resv) } });
        if (!ok) { showMessage(archive ? 'アーカイブできませんでした' : '戻せませんでした', 'error'); return; }
        showMessage(archive ? `${name} をアーカイブしました（新しいメッセージが来たら一覧に戻ります）` : `${name} を一覧に戻しました`);
        if (!archive && !isLpChatInstance(instance)) fetchUsers('', showArchived);
    };
    const renderArchiveButton = (instance, sx = {}) => {
        const archived = isInstanceArchived(instance);
        return (
            <Tooltip title={archived ? '一覧に戻す' : 'アーカイブ（一覧から外す。お客様には影響しません）'}>
                <IconButton
                    size="small"
                    aria-label={archived ? '一覧に戻す' : 'アーカイブ'}
                    onClick={(e) => { e.stopPropagation(); handleArchiveInstance(instance, !archived); }}
                    sx={{ color: archived ? '#16a34a' : '#94a3b8', '&:hover': { color: archived ? '#15803d' : '#f59e0b' }, ...sx }}
                >
                    {archived ? <UnarchiveIcon sx={{ fontSize: 18 }} /> : <ArchiveIcon sx={{ fontSize: 18 }} />}
                </IconButton>
            </Tooltip>
        );
    };

    // ツール（2026-10-26 §13-6）
    const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
    const [unpinTargets, setUnpinTargets] = useState<PastReturnPin[] | null>(null);
    const runBoardTool = (id: string) => {
        if (id === 'unpin_past_returns') {
            const d = new Date();
            const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const all = reservationSyncService.getReservations();
            setUnpinTargets(findPastReturnPins(chatInstances, (rid) => all.find(r => String(r.id) === String(rid)), today));
        }
    };
    /** 選んだ対象のピンを外す。失敗したものの key を返す（ダイアログが名前を出す） */
    const handleUnpinPastReturns = async (targets: PastReturnPin[]): Promise<string[]> => {
        const results = await Promise.all(targets.map(async t => {
            const ok = t.kind === 'line'
                ? await togglePin(t.line_user_id as string, false)
                : await resvChat.togglePin(t.session_key as string, false);
            return { key: t.key, ok: Boolean(ok) };
        }));
        const failed = results.filter(r => !r.ok).map(r => r.key);
        logEvent({ action: 'line_board.unpin_past_returns', result: failed.length ? 'fail' : 'ok', detail: { requested: targets.length, failed: failed.length } });
        return failed;
    };

    // ピン留め: LINE は api_v2（line_users）、自社チャットは LP chat.php（update_session_meta）
    const handleTogglePin = (instance) => {
        if (isLpChatInstance(instance)) { resvChat.togglePin(instance.session_key, !instance.is_pinned); return; }
        togglePin(instance.line_user_id, !instance.is_pinned);
    };
    // アバター: 自社チャットは LINE プロフィールが無いので固定アイコン（LazyAvatar は LINE API の syncProfile を呼ぶため使わない）
    const renderAvatar = (instance, sx) => isLpChatInstance(instance) ? (
        <div className="line-avatar-container">
            {instance.kind === 'anon'
                ? <Avatar sx={{ width: 40, height: 40, bgcolor: '#f5f3ff', color: '#6d28d9', fontSize: 20, ...sx }}>🌐</Avatar>
                : <Avatar sx={{ width: 40, height: 40, bgcolor: '#ecfeff', color: '#0e7490', fontSize: 20, ...sx }}>💬</Avatar>}
            <div className={`link-status-badge ${instance.linked_reservations?.length > 0 ? 'connected' : 'disconnected'}`}>
                {instance.linked_reservations?.length > 0 ? <LinkIcon sx={{ fontSize: 'inherit' }} /> : <LinkOffIcon sx={{ fontSize: 'inherit' }} />}
            </div>
        </div>
    ) : (
        <LazyAvatar user={instance} syncProfile={syncProfile} sx={sx} />
    );
    const resvBadge = (instance) => {
        if (instance.kind === 'resv') {
            return <div className="line-mini-badge" style={{ background: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc' }}>💬予約チャット</div>;
        }
        if (instance.kind === 'anon') {
            const t = instance.triage;
            return (
                <>
                    <div className="line-mini-badge" style={{ background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' }}>🌐匿名チャット</div>
                    {t === 'attention' && <div className="line-mini-badge" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}>要対応</div>}
                    {t === 'junk' && <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>ジャンク</div>}
                    {instance.is_blocked && <div className="line-mini-badge" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}>ブロック中</div>}
                </>
            );
        }
        // 「すべて」タブで同じ予約の予約チャットをまとめた LINE のカード
        if (instance.sibling_resv) {
            return (
                <div className="line-mini-badge" title="同じ予約の予約チャットもあります（開いて切替）"
                    style={instance.sibling_resv_unread
                        ? { background: '#dc2626', color: 'white', border: '1px solid #dc2626' }
                        : { background: '#ecfeff', color: '#0e7490', border: '1px solid #a5f3fc' }}>
                    💬予約チャットも{instance.sibling_resv_unread ? '（新着）' : ''}
                </div>
            );
        }
        return null;
    };
    // 自社チャットの未読数は API が返さない（最後の発言が顧客で既読位置より新しい = 新着）
    const unreadLabel = (instance) => isLpChatInstance(instance) ? '新着' : instance.unread_count;

    const renderChatCard = (instance) => {
        const isSelected = selectedUserIds.includes(instance.instanceKey);
        const user = instance; // For compatibility within the function
        const resData = instance.instance_res_data;
        return (
            <div 
                key={instance.instanceKey} 
                className={`line-chat-card ${user.unread_count > 0 ? 'unread' : ''} ${multiSelectMode && isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                    if (e.metaKey) {
                        handleStaffChatRegistration(instance);
                    } else if (multiSelectMode) {
                        toggleUserSelection(instance.instanceKey);
                    } else {
                        handleUserSelect(instance.instanceKey);
                    }
                }}
            >
                {multiSelectMode && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                        <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            border: '2px solid #cbd5e1',
                            background: isSelected ? '#2563eb' : 'white',
                            borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px'
                        }}>
                            {isSelected && '✓'}
                        </div>
                    </div>
                )}
                
                {/* 左上のバッジ群 */}
                <div className="line-card-badges-top-left">
                    {resvBadge(instance)}
                    {user.hasOtherReservations && (
                        <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
                            他予約有
                        </div>
                    )}
                    {user.reservation_statuses?.map((st, idx) => {
                        const classMap = {
                            '本日貸出': 'today-pickup',
                            '本日返却': 'today-return',
                            '明日貸出': 'tomorrow-pickup',
                            '明日返却': 'tomorrow-return',
                            '利用中': 'in-use'
                        };
                        const cls = classMap[st] || st.toLowerCase().replace(/\s+/g, '-');
                        return (
                            <div key={idx} className={`line-mini-badge ${cls}`}>
                                {st}
                            </div>
                        );
                    })}
                    {user.alert_level > 0 && (
                        <Tooltip title={getAlertTooltip(user.alert_level)}>
                            <div className="line-mini-badge" style={{
                                background: getAlertColor(user.alert_level) || undefined,
                                color: 'white',
                                border: 'none',
                            }}>
                                {getAlertLabel(user.alert_level)}
                            </div>
                        </Tooltip>
                    )}
                </div>

                {user.unread_count > 0 && <div className="line-card-badge">{unreadLabel(user)}</div>}

                <div className="line-card-pin-toggle">
                    {renderArchiveButton(instance)}
                    <Tooltip title={user.is_pinned ? "ピン留めを解除" : "ピン留めする"}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePin(user);
                            }}
                            sx={{
                                color: user.is_pinned ? '#f97316' : '#94a3b8',
                                '&:hover': { color: '#f97316' }
                            }}
                        >
                            <PushPinIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                
                <div className="line-user-header">
                    {renderAvatar(user, { width: 44, height: 44, borderRadius: '14px' })}
                    <div className="line-user-meta">
                        <div className="line-user-name-container">
                            <div className="line-primary-name-row">
                                <div className="line-primary-name">
                                    {resData?.customer_name || user.custom_display_name || user.line_display_name || 'Unknown'}
                                </div>
                            </div>
                            {(() => {
                                const primaryName = resData?.customer_name || user.custom_display_name;
                                const secondaryName = user.line_display_name;
                                return primaryName && secondaryName && primaryName !== secondaryName ? (
                                    <div className="line-secondary-name">
                                        {secondaryName}
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        {instance.instance_reservation_id && resData && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div className="line-usage-period">
                                    {(() => {
                                        const parseDate = (dateStr) => {
                                            if (!dateStr || typeof dateStr !== 'string') return null;
                                            let d = new Date(dateStr);
                                            if (!isNaN(d.getTime())) return d;
                                            const normalized = dateStr.replace(/-/g, '/');
                                            d = new Date(normalized);
                                            return isNaN(d.getTime()) ? null : d;
                                        };

                                        const pDate = resData.pickup_date ? parseDate(resData.pickup_date) : null;
                                        const rDate = resData.return_date ? parseDate(resData.return_date) : null;
                                        
                                        if (pDate && rDate) {
                                            const fmt = (d) => `${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                                            return (
                                                <>
                                                    <span className="line-period-item"><span className="line-period-icon pickup">↑</span>{fmt(pDate)}</span>
                                                    <span className="line-period-item"><span className="line-period-icon return">↓</span>{fmt(rDate)}</span>
                                                </>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', fontSize: '10px', fontWeight: 800, paddingLeft: '4px' }}>
                                    <span style={{ color: (resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '#10b981' : '#ef4444' }}>
                                        {(resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '支払済' : '未払'}
                                    </span>
                                    <span style={{ color: (resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? '#10b981' : '#94a3b8' }}>
                                        {(resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? 'フォームOK' : 'フォーム未対応'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="line-tag-container" style={{ marginBottom: '12px' }}>
                    {user.tags?.map(tag => (
                        <span 
                            key={tag.id} 
                            className="line-tag" 
                            style={{ background: tag.color + '22', color: tag.color, border: `1px solid ${tag.color}44` }}
                        >
                            {tag.name}
                        </span>
                    ))}
                </div>

                <div className="line-card-bubble">
                    <div className={`line-last-message ${(optimisticMessages[user.line_user_id]?.length > 0) ? 'optimistic' : ''}`}>
                        {optimisticMessages[user.line_user_id]?.slice(-1)[0]?.content || user.last_message_content || 'メッセージはありません'}
                    </div>
                </div>
                <div className="line-card-footer">
                    <div className="line-time">
                        {optimisticMessages[user.line_user_id]?.length > 0 
                            ? (optimisticMessages[user.line_user_id].slice(-1)[0].status === 'sent' ? '送信済' : '送信中...') 
                            : (user.last_message_at ? `${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleDateString([], { month: '2-digit', day: '2-digit' })} ${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '---')}
                    </div>
                    {user.is_confirmed && (
                        <div className="line-confirmed">
                            確認済み <CheckCircleIcon sx={{ fontSize: 14 }} />
                        </div>
                    )}
                    {instance.instance_reservation_id && (
                        <div 
                            className="line-detail-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedResForEditor(instance.instance_res_data);
                            }}
                        >
                            詳細
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderListRow = (instance) => {
        const isSelected = selectedUserIds.includes(instance.instanceKey);
        const user = instance;
        const resData = instance.instance_res_data;
        return (
            <div 
                key={instance.instanceKey} 
                className={`line-list-item ${user.unread_count > 0 ? 'unread' : ''} ${multiSelectMode && isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                    if (e.metaKey) {
                        handleStaffChatRegistration(instance);
                    } else if (multiSelectMode) {
                        toggleUserSelection(instance.instanceKey);
                    } else {
                        handleUserSelect(instance.instanceKey);
                    }
                }}
            >
                {multiSelectMode && (
                    <div style={{ marginRight: '15px' }}>
                        <div style={{ 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            border: '2px solid #cbd5e1',
                            background: isSelected ? '#2563eb' : 'white',
                            borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px'
                        }}>
                            {isSelected && '✓'}
                        </div>
                    </div>
                )}
                <div className="line-list-left">
                    {renderAvatar(user, { width: 40, height: 40, borderRadius: '10px' })}
                        <div className="line-list-names">
                            <div className="line-primary-name-row">
                                <div className="line-primary-name">
                                    {resData?.customer_name || user.custom_display_name || user.line_display_name || 'Unknown'}
                                </div>
                            </div>
                            {instance.instance_reservation_id && resData && (
                                <>
                                    <div className="line-usage-period" style={{ marginTop: '0', background: 'transparent', padding: '0', fontSize: '11px' }}>
                                        {(() => {
                                            const parseDate = (dateStr) => {
                                                if (!dateStr || typeof dateStr !== 'string') return null;
                                                // Try direct parse (works for ISO8601)
                                                let d = new Date(dateStr);
                                                if (!isNaN(d.getTime())) return d;
                                                
                                                // Fallback for MySQL format (YYYY-MM-DD HH:MM:SS) -> (YYYY/MM/DD HH:MM:SS)
                                                const normalized = dateStr.replace(/-/g, '/');
                                                d = new Date(normalized);
                                                return isNaN(d.getTime()) ? null : d;
                                            };

                                            const pDate = resData.pickup_date ? parseDate(resData.pickup_date) : null;
                                            const rDate = resData.return_date ? parseDate(resData.return_date) : null;
                                            
                                            if (pDate && rDate) {
                                                const fmt = (d) => `${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                                                return (
                                                    <>
                                                        <span className="line-period-item"><span className="line-period-icon pickup">↑</span>{fmt(pDate)}</span>
                                                        <span className="line-period-item"><span className="line-period-icon return">↓</span>{fmt(rDate)}</span>
                                                    </>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '10px', fontWeight: 800, paddingLeft: '0', marginTop: '2px' }}>
                                        <span style={{ color: (resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '#10b981' : '#ef4444' }}>
                                            {(resData.payment === true || resData.payment === 1 || resData.payment === '1') ? '支払済' : '未払'}
                                        </span>
                                        <span style={{ color: (resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? '#10b981' : '#94a3b8' }}>
                                            {(resData.agreement === true || resData.agreement === 1 || resData.agreement === '1') ? 'フォームOK' : 'フォーム未対応'}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="line-secondary-name">{user.line_display_name}</div>
                        </div>
                    </div>

                <div className="line-list-badges">
                    {resvBadge(instance)}
                    {user.is_pinned && (
                        <div className="line-mini-badge pinned">ピン</div>
                    )}
                    {user.hasOtherReservations && (
                        <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>他予約有</div>
                    )}
                    {user.reservation_statuses?.map((st, idx) => {
                        const classMap = {
                            '本日貸出': 'today-pickup',
                            '本日返却': 'today-return',
                            '明日貸出': 'tomorrow-pickup',
                            '明日返却': 'tomorrow-return',
                            '利用中': 'in-use'
                        };
                        const cls = classMap[st] || st.toLowerCase().replace(/\s+/g, '-');
                        return (
                            <div key={idx} className={`line-mini-badge ${cls}`}>
                                {st}
                            </div>
                        );
                    })}
                    {user.alert_level > 0 && (
                        <Tooltip title={getAlertTooltip(user.alert_level)}>
                            <div className="line-mini-badge" style={{
                                background: getAlertColor(user.alert_level) || undefined,
                                color: 'white',
                                border: 'none',
                            }}>
                                {getAlertLabel(user.alert_level)}
                            </div>
                        </Tooltip>
                    )}
                </div>

                <div className="line-list-message-cell">
                    <div className="line-last-message">
                        {optimisticMessages[user.line_user_id]?.slice(-1)[0]?.content || user.last_message_content || '---'}
                    </div>
                </div>

                <div className="line-list-right">
                    {instance.instance_reservation_id && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedResForEditor(instance.instance_res_data);
                            }}
                            sx={{ mr: 1, borderRadius: '6px', fontSize: '11px', py: 0, minWidth: 'auto', px: 1, height: '24px' }}
                        >
                            詳細
                        </Button>
                    )}
                    <div className="line-list-time">
                        {user.last_message_at ? `${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleDateString([], { month: '2-digit', day: '2-digit' })} ${new Date(user.last_message_at.replace(/-/g, '/')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                    {user.unread_count > 0 && <div className="line-list-unread-badge">{unreadLabel(user)}</div>}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePin(user);
                        }}
                        sx={{ color: user.is_pinned ? '#f97316' : '#cbd5e1', ml: 0.5 }}
                    >
                        <PushPinIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    {renderArchiveButton(instance, { ml: 0.25 })}
                </div>
            </div>
        );
    };

    return (
        <div className="line-chat-board">
            {/* Auto-archive toasts */}
            {archiveToasts.length > 0 && (
                <Box sx={{
                    position: 'fixed', top: 16, right: 16, zIndex: 13000,
                    display: 'flex', flexDirection: 'column', gap: 1,
                    pointerEvents: 'none',
                }}>
                    {archiveToasts.map((t) => (
                        <Box key={t.id} sx={{
                            background: '#fffbeb', border: '1px solid #fcd34d',
                            borderRadius: '10px', px: 2, py: 1, boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                            display: 'flex', alignItems: 'center', gap: 1,
                            animation: 'slideInRight 0.25s ease-out',
                            pointerEvents: 'auto',
                        }}>
                            <ArchiveIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e', fontSize: '13px' }}>
                                {t.name} がアーカイブされました。
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <style>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(40px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>

            <header className="line-board-header">
                <div className="line-board-title">
                    <ChatIcon color="primary" />
                    <span>チャット・ボード</span>
                    <Tooltip title="使い方ガイド">
                        <IconButton 
                            onClick={() => setShowBoardHelp(true)} 
                            sx={{ color: '#64748b', ml: 1 }}
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                
                <IconButton 
                    className="line-mobile-menu-trigger"
                    onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                    sx={{ ml: 'auto' }}
                >
                    <MoreVertIcon />
                </IconButton>

                <div className="line-board-controls">
                    <Box display="flex" alignItems="center" gap={3}>
{lineEnabled && (/* LINE 固有: AI返信モード */
                        <Box display="flex" alignItems="center" sx={{
                            background: aiReplyMode === 0 ? '#f1f5f9' : aiReplyMode === 1 ? '#dbeafe' : '#dcfce7',
                            px: 2, py: 0.5, borderRadius: '20px',
                            border: aiReplyMode > 0 ? `1px solid ${aiReplyMode === 1 ? '#93c5fd' : '#86efac'}` : '1px solid transparent',
                        }}>
                            <Typography variant="caption" sx={{ mr: 0.5, fontWeight: 800, color: aiReplyMode === 0 ? '#64748b' : aiReplyMode === 1 ? '#2563eb' : '#16a34a' }}>
                                AI返信
                            </Typography>
                            <Select
                                size="small"
                                value={aiReplyMode}
                                onChange={(e) => handleAiReplyModeChange(Number(e.target.value))}
                                sx={{ fontSize: '11px', height: '24px', '& .MuiSelect-select': { py: 0, pr: '20px !important', pl: '4px' } }}
                                variant="standard"
                                disableUnderline
                            >
                                <MuiMenuItem value={0}>OFF</MuiMenuItem>
                                <MuiMenuItem value={1}>テンプレのみ</MuiMenuItem>
                                <MuiMenuItem value={2}>全自動</MuiMenuItem>
                            </Select>
                        </Box>
)}
                        <Box display="flex" alignItems="center" sx={{ background: '#f1f5f9', px: 2, py: 0.5, borderRadius: '20px' }}>
                            <Typography variant="caption" sx={{ mr: 1, fontWeight: 800, color: '#64748b' }}>
                                既読モード
                            </Typography>
                            <Switch
                                size="small"
                                checked={readMode}
                                onChange={(e) => setReadMode(e.target.checked)}
                            />
                        </Box>
                    </Box>
                    
                    <div className="line-view-toggle">
                        <IconButton 
                            size="small" 
                            onClick={() => setViewMode('grid')}
                            className={viewMode === 'grid' ? 'active' : ''}
                            sx={{ color: viewMode === 'grid' ? '#2563eb' : '#94a3b8' }}
                        >
                            <ViewComfyIcon />
                        </IconButton>
                        <IconButton 
                            size="small" 
                            onClick={() => setViewMode('list')}
                            className={viewMode === 'list' ? 'active' : ''}
                            sx={{ color: viewMode === 'list' ? '#2563eb' : '#94a3b8' }}
                        >
                            <ViewListIcon />
                        </IconButton>
                    </div>

{lineEnabled && (/* LINE 固有: 複数選択・一斉送信 */
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {!multiSelectMode ? (
                            <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => { setMultiSelectMode(true); setSelectedUserIds([]); }}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#e2e8f0', color: '#1e293b' }}
                            >
                                複数選択
                            </Button>
                        ) : (
                            <>
                                <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={handleSelectAll}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, mr: 1 }}
                                >
                                    全選択
                                </Button>
                                <Button 
                                    variant="contained" 
                                    size="small"
                                    color="primary"
                                    disabled={selectedUserIds.length === 0}
                                    onClick={() => { 
                                        loadTemplatesData();
                                        setShowBroadcastModal(true); 
                                    }}
                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    一斉送信 ({selectedUserIds.length})
                                </Button>
                                <Button 
                                    variant="text" 
                                    size="small"
                                    onClick={() => setMultiSelectMode(false)}
                                    sx={{ color: '#64748b' }}
                                >
                                    キャンセル
                                </Button>
                            </>
                        )}
                    </div>
)}
                    
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />} 
                        onClick={handleRefresh}
                        className="RefreshButton"
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#e2e8f0', color: '#1e293b' }}
                    >
                        最新の状態に更新
                    </Button>

                    <Menu
                        anchorEl={mobileMenuAnchor}
                        open={Boolean(mobileMenuAnchor)}
                        onClose={() => setMobileMenuAnchor(null)}
                        PaperProps={{
                            sx: { borderRadius: '12px', minWidth: '200px', mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                        }}
                    >
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography variant="body2" fontWeight={800}>AI返信</Typography>
                                <Select
                                    size="small"
                                    value={aiReplyMode}
                                    onChange={(e) => handleAiReplyModeChange(Number(e.target.value))}
                                    sx={{ fontSize: '12px', height: '28px', ml: 1 }}
                                    variant="standard"
                                    disableUnderline
                                >
                                    <MuiMenuItem value={0}>OFF</MuiMenuItem>
                                    <MuiMenuItem value={1}>テンプレのみ</MuiMenuItem>
                                    <MuiMenuItem value={2}>全自動</MuiMenuItem>
                                </Select>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography variant="body2" fontWeight={800}>既読モード</Typography>
                                <Switch
                                    size="small"
                                    checked={readMode}
                                    onChange={(e) => setReadMode(e.target.checked)}
                                />
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography variant="body2" fontWeight={800}>定期読み込み</Typography>
                                <Switch 
                                    size="small"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                />
                            </Box>
                        </MuiMenuItem>
{lineEnabled && (/* LINE 固有: 複数選択(モバイル) */
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setMultiSelectMode(!multiSelectMode); setSelectedUserIds([]); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircleIcon fontSize="small" color={multiSelectMode ? "primary" : "inherit"} />
                                <Typography variant="body2" fontWeight={800}>{multiSelectMode ? '複数選択解除' : '複数選択モード'}</Typography>
                            </Box>
                        </MuiMenuItem>
)}
                        {isMobile && multiSelectMode && (
                            <MuiMenuItem 
                                disabled={selectedUserIds.length === 0}
                                onClick={() => { 
                                    setMobileMenuAnchor(null);
                                    loadTemplatesData();
                                    setShowBroadcastModal(true); 
                                }}
                                sx={{ color: '#2563eb' }}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <SendIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={800}>一斉送信 ({selectedUserIds.length})</Typography>
                                </Box>
                            </MuiMenuItem>
                        )}
{lineEnabled && (/* LINE 固有: テンプレート管理(モバイル) */
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setShowTemplateManager(true); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SettingsSuggestIcon fontSize="small" />
                                <Typography variant="body2" fontWeight={800}>テンプレート管理</Typography>
                            </Box>
                        </MuiMenuItem>
)}
{lineEnabled && (/* LINE 固有: AIチャットレビュー(モバイル) */
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); window.open('/ai_chat_review', '_blank'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SmartToyIcon fontSize="small" />
                                <Typography variant="body2" fontWeight={800}>AIチャットレビュー</Typography>
                            </Box>
                        </MuiMenuItem>
)}
{lineEnabled && (/* LINE 固有: AIアクションログ(モバイル) */
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); window.open('/ai_action_log', '_blank'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SmartToyIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                                <Typography variant="body2" fontWeight={800}>AIアクションログ</Typography>
                            </Box>
                        </MuiMenuItem>
)}
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setViewMode(viewMode === 'grid' ? 'list' : 'grid'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewComfyIcon fontSize="small" />}
                                <Typography variant="body2" fontWeight={800}>{viewMode === 'grid' ? 'リスト表示に切替' : 'グリッド表示に切替'}</Typography>
                            </Box>
                        </MuiMenuItem>
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); handleRefresh(); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <RefreshIcon fontSize="small" />
                                <Box>
                                    <Typography variant="body2" fontWeight={800}>最新の状態に更新</Typography>
                                    {showArchived && (
                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, lineHeight: 1 }}>（アーカイブを含む）</Typography>
                                    )}
                                </Box>
                            </Box>
                        </MuiMenuItem>
                        {BOARD_TOOLS.map(t => (
                            <MuiMenuItem key={t.id} onClick={() => { setMobileMenuAnchor(null); runBoardTool(t.id); }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <BuildIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={800}>{t.label}</Typography>
                                </Box>
                            </MuiMenuItem>
                        ))}
                        {/* アーカイブ表示は一覧の上の「アーカイブ」タブに移した（2026-10-26 §13-5） */}
                        <MuiMenuItem onClick={() => { setMobileMenuAnchor(null); setKindTab('archive'); }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <ArchiveIcon fontSize="small" color={showArchived ? "warning" : "inherit"} />
                                <Typography variant="body2" fontWeight={800}>アーカイブを見る</Typography>
                            </Box>
                        </MuiMenuItem>
                    </Menu>
                </div>
            </header>

            <div className="line-filters-container">
                <Box display="flex" flexDirection="column" gap={2} mb={1}>
                    {/* 種類タブ・匿名の区分・受信音・自社チャットの受付と通知（/admin の統合・2026-10-26） */}
                    {(
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <div className="line-filter-chips">
                                {KIND_TABS.filter(t => (lineEnabled || t.value !== 'line') && (resvChatEnabled || (t.value !== 'resv' && t.value !== 'anon'))).map(t => {
                                    // アーカイブの件数は「アーカイブ」タブを開いて読み込んだときだけ分かる
                                    const n = t.value === 'all' ? kindCounts.all : t.value === 'archive' ? (showArchived ? kindCounts.archive : null) : kindCounts[t.value];
                                    return (
                                        <div key={t.value} className={`line-filter-chip ${kindTab === t.value ? 'active' : ''}`} onClick={() => setKindTab(t.value)}>
                                            {t.value === 'archive' && <ArchiveIcon sx={{ fontSize: 14 }} />}
                                            {t.label} {n !== null && <span style={{ opacity: 0.7, marginLeft: 2 }}>{n}</span>}
                                            {t.value === 'anon' && kindCounts.attentionUnreplied > 0 && (
                                                <span style={{ marginLeft: 4, background: '#dc2626', color: 'white', borderRadius: 9999, padding: '0 6px', fontSize: 10, fontWeight: 800 }}
                                                    title="匿名チャットの要対応で、最後の発言がお客様">要対応 {kindCounts.attentionUnreplied}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {kindTab === 'anon' && (
                                <div className="line-filter-chips">
                                    {ANON_TRIAGE_TABS.map(t => {
                                        const n = t.value === 'all' ? kindCounts.anon : kindCounts[t.value];
                                        return (
                                            <div key={t.value} className={`line-filter-chip ${anonTriage === t.value ? 'active' : ''}`} onClick={() => setAnonTriage(t.value)}
                                                style={{ fontSize: 11 }}>
                                                {t.label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{n}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ ml: 'auto', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                                {soundBlocked && soundOn && (
                                    <span style={{ color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '2px 8px', fontSize: 11 }}>
                                        ※音が鳴らない場合は、画面を一度クリックしてください
                                    </span>
                                )}
                                <Tooltip title="新しいメッセージ（LINE・予約チャット・匿名チャットの要対応）で音を鳴らす（この端末）">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        通知音
                                        <Switch size="small" checked={soundOn} onChange={(e) => setChatSoundEnabled(e.target.checked)} inputProps={{ 'aria-label': '通知音' }} />
                                    </Box>
                                </Tooltip>
                                <Button size="small" variant="text" sx={{ textTransform: 'none', fontSize: 11, minWidth: 0, px: 0.5 }}
                                    onClick={async () => { const ok = await playChatSoundCheck(); if (!ok) showMessage('音を出せませんでした（ブラウザ・端末の音量や設定を確認してください）', 'error'); }}>
                                    サウンドチェック
                                </Button>
                                {resvChatEnabled && (<>
                                <Tooltip title="LP のチャット受付（OFF の間は AI が一次対応。営業時間の自動切替は cron で別に動く）">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        自社チャット受付
                                        <Switch size="small" checked={Boolean(chatAccepting)} disabled={chatAccepting === null || chatAcceptingBusy}
                                            onChange={handleToggleChatAccepting} inputProps={{ 'aria-label': '自社チャット受付' }} />
                                        {chatAccepting === null && <span style={{ color: '#b91c1c' }}>状態不明</span>}
                                    </Box>
                                </Tooltip>
                                {pushState !== 'unsupported' && (
                                    <Tooltip title={pushState === 'denied' ? 'ブラウザで通知が拒否されています（ブラウザの設定から許可してください）' : '自社チャットの新着をタブを閉じても OS の通知で知らせる（この端末）'}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            デスクトップ通知
                                            <Switch size="small" checked={pushState === 'on'} disabled={pushBusy || pushState === 'denied'}
                                                onChange={handleTogglePush} inputProps={{ 'aria-label': 'デスクトップ通知' }} />
                                        </Box>
                                    </Tooltip>
                                )}
                                </>)}
                            </Box>
                        </Box>
                    )}
                    {/* 1行目: フィルターチップ */}
                    <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                        <div className="line-filter-chips">
                            {[
                                { id: 'all', label: 'すべて', icon: <SearchIcon fontSize="small" /> },
                                { id: 'unread', label: '未読', icon: <MarkChatUnreadIcon fontSize="small" /> },
                                { id: 'pinned', label: 'ピン留め', icon: <PushPinIcon fontSize="small" /> },
                                { id: 'today_pickup', label: '本日貸出', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'today_return', label: '本日返却', icon: <CheckCircleIcon fontSize="small" /> },
                                { id: 'tomorrow_pickup', label: '翌日貸出', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'tomorrow_return', label: '翌日返却', icon: <CheckCircleIcon fontSize="small" /> },
                                { id: 'in_use', label: '貸出中', icon: <RefreshIcon fontSize="small" /> },
                                { id: 'unlinked', label: '未紐付け', icon: <PersonIcon fontSize="small" /> },
                            ].map(chip => (
                                <div 
                                    key={chip.id} 
                                    className={`line-filter-chip ${
                                        chip.id === 'all'
                                            ? (filters.length === 0 && !customDateFilter && selectedTagFilters.length === 0 ? 'active' : '')
                                            : (filters.includes(chip.id) ? 'active' : '')
                                    }`}
                                    onClick={() => { toggleFilter(chip.id); if (chip.id === 'all') setSelectedTagFilters([]); }}
                                >
                                    {chip.icon}
                                    {chip.label}
                                </div>
                            ))}

                            {/* Tag filter chip */}
{lineEnabled && (/* LINE 固有: タグ */
                            <div
                                className={`line-filter-chip ${selectedTagFilters.length > 0 ? 'active' : ''}`}
                                onClick={(e) => setTagFilterAnchor(e.currentTarget)}
                            >
                                <LocalOfferIcon fontSize="small" />
                                {selectedTagFilters.length > 0 ? `タグ (${selectedTagFilters.length})` : 'タグ'}
                            </div>
)}
                            <Popover
                                open={Boolean(tagFilterAnchor)}
                                anchorEl={tagFilterAnchor}
                                onClose={() => setTagFilterAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                PaperProps={{ sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', minWidth: 200 } }}
                            >
                                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>タグで絞り込み</Typography>
                                {tags && tags.length > 0 ? (
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                        {tags.map(tag => {
                                            const isSelected = selectedTagFilters.includes(tag.id);
                                            return (
                                                <Box
                                                    key={tag.id}
                                                    onClick={() => setSelectedTagFilters(prev =>
                                                        isSelected ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                                                    )}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        px: 1.5, py: 0.8, borderRadius: '8px', cursor: 'pointer',
                                                        background: isSelected ? tag.color + '18' : 'transparent',
                                                        border: isSelected ? `1px solid ${tag.color}` : '1px solid transparent',
                                                        '&:hover': { background: tag.color + '10' },
                                                    }}
                                                >
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: tag.color, flexShrink: 0 }} />
                                                    <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>{tag.name}</Typography>
                                                </Box>
                                            );
                                        })}
                                        {selectedTagFilters.length > 0 && (
                                            <Button size="small" onClick={() => setSelectedTagFilters([])} sx={{ mt: 0.5, fontWeight: 700 }}>
                                                クリア
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">タグがありません</Typography>
                                )}
                            </Popover>

                            <div
                                className={`line-filter-chip ${customDateFilter ? 'active' : ''}`}
                                onClick={(e) => setDateAnchor(e.currentTarget)}
                            >
                                <CalendarTodayIcon fontSize="small" />
                                {customDateFilter ? `${customDateFilter.date} (${customDateFilter.type === 'pickup' ? '貸出' : '返却'})` : '日付選択'}
                            </div>

                            <Popover
                                open={Boolean(dateAnchor)}
                                anchorEl={dateAnchor}
                                onClose={() => setDateAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                PaperProps={{ sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' } }}
                            >
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Typography variant="subtitle2" fontWeight={800}>日付を指定して絞り込み</Typography>
                                    <input 
                                        type="date" 
                                        value={tempDate} 
                                        onChange={(e) => setTempDate(e.target.value)}
                                        style={{ 
                                            padding: '8px', 
                                            borderRadius: '8px', 
                                            border: '1px solid #cbd5e1',
                                            fontSize: '14px',
                                            fontFamily: 'inherit'
                                        }}
                                    />
                                    <Box display="flex" gap={1}>
                                        <Button 
                                            variant="contained" 
                                            fullWidth 
                                            size="small"
                                            onClick={() => {
                                                setCustomDateFilter({ date: tempDate, type: 'pickup' });
                                                setDateAnchor(null);
                                            }}
                                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                                        >
                                            貸出
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            fullWidth 
                                            size="small"
                                            onClick={() => {
                                                setCustomDateFilter({ date: tempDate, type: 'return' });
                                                setDateAnchor(null);
                                            }}
                                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                                        >
                                            返却
                                        </Button>
                                    </Box>
                                    <Button 
                                        size="small" 
                                        color="error" 
                                        onClick={() => {
                                            setCustomDateFilter(null);
                                            setDateAnchor(null);
                                        }}
                                    >
                                        解除
                                    </Button>
                                </Box>
                            </Popover>
                        </div>
                    </Box>

                    {/* 2行目: 検索窓とテンプレート管理 */}
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <div className="line-search-wrapper" style={{ margin: 0, flex: 1, minWidth: '300px' }}>
                            <SearchIcon className="line-search-icon" />
                            <input 
                                type="text" 
                                className="line-search-input" 
                                placeholder={lineEnabled ? "顧客名・LINE名・連絡コード・メッセージを検索.." : "顧客名・連絡コード・メッセージを検索.."} 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        {searchIncludedArchived && (
                            <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                アーカイブからも検索しました
                            </Typography>
                        )}
{lineEnabled && (/* LINE 固有: テンプレート管理 */
                        <Button
                            variant="outlined"
                            startIcon={<SettingsSuggestIcon />}
                            onClick={() => setShowTemplateManager(true)}
                            color="info"
                            className="desktop-only"
                            sx={{ 
                                borderRadius: '12px', 
                                textTransform: 'none', 
                                fontWeight: 800, 
                                height: '45px', 
                                border: '1px solid #e2e8f0', 
                                background: 'white',
                                color: '#1e293b',
                                whiteSpace: 'nowrap',
                                ml: 2
                            }}
                        >
                            テンプレート管理
                        </Button>
)}
                        {/* ツール（いろいろな操作のメニュー。項目は lib/boardTools の BOARD_TOOLS に足す・2026-10-26 §13-6） */}
                        <Button
                            variant="outlined"
                            startIcon={<BuildIcon />}
                            onClick={(e) => setToolsAnchor(e.currentTarget)}
                            className="desktop-only"
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 800, height: '45px',
                                border: '1px solid #e2e8f0', background: 'white', color: '#1e293b', whiteSpace: 'nowrap', ml: 1,
                            }}
                        >
                            ツール
                        </Button>
                        <Menu anchorEl={toolsAnchor} open={Boolean(toolsAnchor)} onClose={() => setToolsAnchor(null)}>
                            {BOARD_TOOLS.map(t => (
                                <MuiMenuItem key={t.id} onClick={() => { setToolsAnchor(null); runBoardTool(t.id); }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={800}>{t.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                                    </Box>
                                </MuiMenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>
            </div>
            <UnpinPastReturnsDialog
                open={unpinTargets !== null}
                targets={unpinTargets || []}
                onClose={() => setUnpinTargets(null)}
                onUnpin={handleUnpinPastReturns}
            />

            {loading && chatInstances.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>読み込み中...</Box>
            ) : (
                <div className={viewMode === 'grid' ? "line-chat-grid" : "line-chat-list"}>
                    {boardInstances.slice(0, visibleCount).map(instance => (
                        viewMode === 'grid' ? renderChatCard(instance) : renderListRow(instance)
                    ))}
                </div>
            )}

            {isMobile && multiSelectMode && (
                <div className="line-mobile-sticky-bar">
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                            {selectedUserIds.length} 名を選択中
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button 
                                variant="contained" 
                                color="success"
                                size="small"
                                disabled={selectedUserIds.length === 0}
                                onClick={() => { 
                                    loadTemplatesData();
                                    setShowBroadcastModal(true); 
                                }}
                                startIcon={<SendIcon />}
                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 800, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                            >
                                一斉送信
                            </Button>
                            <IconButton 
                                size="small" 
                                onClick={() => setMultiSelectMode(false)}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </div>
            )}

            {/* Template Manager Dialog */}
            <Dialog 
                open={showTemplateManager} 
                onClose={() => setShowTemplateManager(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <SettingsSuggestIcon color="primary" />
                            <span>メッセージテンプレート管理</span>
                        </Box>
                        <IconButton onClick={() => setShowTemplateManager(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ display: 'flex', gap: 0, height: '600px' }}>
                        
                        {/* 1. カテゴリ管理 (左カラム) */}
                        <div style={{ width: '220px', borderRight: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
                            <Box p={2} borderBottom="1px solid #e2e8f0" display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={800}>カテゴリ</Typography>
                                <IconButton size="small" onClick={() => { setEditCategory({ id: null, name: '' }); setShowCategoryForm(true); }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            
                            {showCategoryForm && (
                                <Box p={2} borderBottom="1px solid #e2e8f0" bgcolor="white">
                                    <TextField 
                                        size="small" 
                                        fullWidth 
                                        label="カテゴリ名"
                                        value={editCategory.name}
                                        onChange={(e) => setEditCategory(prev => ({ ...prev, name: e.target.value }))}
                                        autoFocus
                                    />
                                    <Box mt={1} display="flex" gap={1}>
                                        <Button size="small" variant="contained" fullWidth onClick={handleSaveCategory}>保存</Button>
                                        <Button size="small" variant="outlined" fullWidth onClick={() => setShowCategoryForm(false)}>消去</Button>
                                    </Box>
                                </Box>
                            )}

                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                <div 
                                    onClick={() => setSelectedCategoryId(null)}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        marginBottom: '4px',
                                        background: selectedCategoryId === null ? '#eef2ff' : 'transparent',
                                        color: selectedCategoryId === null ? '#4f46e5' : '#475569',
                                        fontWeight: selectedCategoryId === null ? 800 : 400,
                                        fontSize: '14px'
                                    }}
                                >
                                    すべて表示
                                </div>
                                <div 
                                    onClick={() => setSelectedCategoryId('none')}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        marginBottom: '4px',
                                        background: selectedCategoryId === 'none' ? '#eef2ff' : 'transparent',
                                        color: selectedCategoryId === 'none' ? '#4f46e5' : '#475569',
                                        fontWeight: selectedCategoryId === 'none' ? 800 : 400,
                                        fontSize: '14px'
                                    }}
                                >
                                    未分類
                                </div>
                                
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                                    <SortableContext items={categories.map(c => String(c.id))} strategy={verticalListSortingStrategy}>
                                        {categories.map(cat => (
                                            <CategorySortableItem 
                                                key={cat.id} 
                                                id={String(cat.id)} 
                                                name={cat.name}
                                                isSelected={selectedCategoryId === cat.id}
                                                onClick={() => setSelectedCategoryId(cat.id)}
                                                onEdit={() => { setEditCategory(cat); setShowCategoryForm(true); }}
                                                onDelete={() => handleDeleteCategory(cat.id)}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>

                        {/* 2. テンプレート一覧 (中カラム) */}
                        <div style={{ width: '280px', borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column' }}>
                            <Box p={2} borderBottom="1px solid #f1f5f9" display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={800}>テンプレート</Typography>
                                <Button 
                                    size="small" 
                                    variant="contained" 
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedTemplate(null);
                                        setEditTemplate({ name: '', content: '', category_id: selectedCategoryId === 'none' ? null : selectedCategoryId });
                                    }}
                                    sx={{ borderRadius: '8px', fontWeight: 800, textTransform: 'none' }}
                                >
                                    新規
                                </Button>
                            </Box>
                            
                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                                    <SortableContext 
                                        items={templates
                                            .filter(t => 
                                                selectedCategoryId === null ? true : 
                                                selectedCategoryId === 'none' ? t.category_id === null : 
                                                String(t.category_id) === String(selectedCategoryId)
                                            )
                                            .sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0))
                                            .map(t => String(t.id))} 
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {templates
                                            .filter(t => 
                                                selectedCategoryId === null ? true : 
                                                selectedCategoryId === 'none' ? t.category_id === null : 
                                                String(t.category_id) === String(selectedCategoryId)
                                            )
                                            .sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0))
                                            .map(t => (
                                                <SortableItem
                                                    key={t.id}
                                                    id={String(t.id)}
                                                    isSelected={selectedTemplate?.id === t.id}
                                                    onClick={() => {
                                                        setSelectedTemplate(t);
                                                        setEditTemplate({ ...t });
                                                    }}
                                                    onDelete={undefined as any}
                                                >
                                                    <div style={{ fontWeight: 800, fontSize: '14px', color: '#1e293b' }}>{t.name}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {t.content}
                                                    </div>
                                                </SortableItem>
                                            ))
                                        }
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>

                        {/* 3. 編集エリア (右カラム) */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px', gap: '20px' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6" fontWeight={800} color="primary">
                                        {selectedTemplate ? 'テンプレート編集' : '新規テンプレート作成'}
                                    </Typography>
                                    <Tooltip title="テンプレートの使い方">
                                        <IconButton size="small" onClick={() => setShowHelp(true)}>
                                            <HelpOutlineIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            
                            <Box display="flex" gap={2}>
                                <TextField 
                                    label="テンプレート名"
                                    fullWidth
                                    value={editTemplate.name}
                                    onChange={(e) => setEditTemplate(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="例: 到着連絡"
                                />
                                <FormControl sx={{ minWidth: 150 }}>
                                    <InputLabel>カテゴリ</InputLabel>
                                    <Select
                                        value={editTemplate.category_id || ''}
                                        label="カテゴリ"
                                        onChange={(e) => setEditTemplate(prev => ({ ...prev, category_id: e.target.value || null }))}
                                    >
                                        <MuiMenuItem value=""><em>未分類</em></MuiMenuItem>
                                        {categories.map(c => (
                                            <MuiMenuItem key={c.id} value={c.id}>{c.name}</MuiMenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Box flex={1} display="flex" flexDirection="column">
                                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" fontWeight={800} color="textSecondary">内容</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        変数: {'{customer_name}, {start_date_time}, {end_date_time}, {form}'}
                                    </Typography>
                                </Box>
                                <textarea 
                                    rows={8}
                                    value={editTemplate.content}
                                    onChange={(e) => setEditTemplate(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder="メッセージ内容を入力..."
                                    style={{ 
                                        width: '100%', 
                                        padding: '16px', 
                                        borderRadius: '12px', 
                                        border: '1px solid #cbd5e1', 
                                        fontSize: '15px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        lineHeight: 1.6,
                                        boxSizing: 'border-box',
                                        minHeight: '200px'
                                    }}
                                />
                            </Box>

                            <Box display="flex" justifyContent="space-between" pt={2} borderTop="1px solid #f1f5f9">
                                {selectedTemplate && (
                                    <Button 
                                        color="error" 
                                        variant="outlined" 
                                        startIcon={<DeleteIcon />}
                                        onClick={async () => {
                                            if (window.confirm('このテンプレートを削除しますか？')) {
                                                const success = await deleteTemplate(selectedTemplate.id);
                                                if (success) {
                                                    showMessage('テンプレートを削除しました');
                                                    loadTemplatesData();
                                                    setSelectedTemplate(null);
                                                    setEditTemplate({ name: '', content: '', category_id: null });
                                                } else {
                                                    showMessage('削除に失敗しました', 'error');
                                                }
                                            }
                                        }}
                                        sx={{ borderRadius: '10px' }}
                                    >
                                        削除
                                    </Button>
                                )}
                                <Box flex={1} />
                                <Button 
                                    variant="contained" 
                                    disabled={!editTemplate.name.trim() || !editTemplate.content.trim()}
                                    onClick={async () => {
                                        const success = await saveTemplate(editTemplate);
                                        if (success) {
                                            showMessage('保存しました');
                                            loadTemplatesData();
                                            // 保存後にフォームをクリア
                                            setSelectedTemplate(null);
                                            setEditTemplate({ name: '', content: '', category_id: null });
                                        } else {
                                            showMessage('保存に失敗しました', 'error');
                                        }
                                    }}
                                    sx={{ borderRadius: '10px', px: 6, py: 1, fontWeight: 800, fontSize: '16px' }}
                                >
                                    保存する
                                </Button>
                            </Box>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Broadcast Modal */}
            <Dialog
                open={showBroadcastModal}
                onClose={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }}
                maxWidth={broadcastStep === 'preview' ? 'lg' : 'sm'}
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <span>
                            {broadcastStep === 'compose'
                                ? `一斉メッセージ送信 (${selectedUserIds.length}名)`
                                : `送信プレビュー (${preparedMessages.length}名)`
                            }
                        </span>
                        <IconButton onClick={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {broadcastStep === 'compose' ? (
                        <>
                            <Box mt={2} mb={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>テンプレートを選択</InputLabel>
                                    <Select
                                        value=""
                                        label="テンプレートを選択"
                                        onChange={(e) => {
                                            const template = templates.find(t => t.id === e.target.value);
                                            if (template) setBroadcastText(template.content);
                                        }}
                                    >
                                        <MuiMenuItem value="">
                                            <em>選択なし</em>
                                        </MuiMenuItem>
                                        {categories.map(cat => [
                                            <MuiMenuItem key={`cat-${cat.id}`} disabled sx={{ fontWeight: 800, background: '#f8fafc', color: '#64748b' }}>
                                                {cat.name}
                                            </MuiMenuItem>,
                                            ...templates
                                                .filter(t => t.category_id == cat.id)
                                                .map(t => (
                                                    <MuiMenuItem key={t.id} value={t.id} sx={{ pl: 4 }}>
                                                        {t.name}
                                                    </MuiMenuItem>
                                                ))
                                        ])}
                                        <MuiMenuItem disabled sx={{ fontWeight: 800, background: '#f8fafc', color: '#64748b' }}>
                                            未分類
                                        </MuiMenuItem>
                                        {templates
                                            .filter(t => !t.category_id)
                                            .map(t => (
                                                <MuiMenuItem key={t.id} value={t.id} sx={{ pl: 4 }}>
                                                    {t.name}
                                                </MuiMenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Box>
                            <div style={{ padding: '0 0' }}>
                                <textarea
                                    rows={8}
                                    value={broadcastText}
                                    onChange={(e) => setBroadcastText(e.target.value)}
                                    placeholder="送信するメッセージを入力してください..."
                                    style={{
                                        width: '100%',
                                        minHeight: '200px',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        marginBottom: '8px',
                                        resize: 'vertical'
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                    ※ {`{customer_name}`}, {`{end_date_time}`} などの変数は送信時に各ユーザーの情報に置換されます。
                                </Typography>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => { setShowBroadcastModal(false); setBroadcastStep('compose'); }}
                                    >
                                        キャンセル
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!broadcastText.trim()}
                                        onClick={handleShowPreview}
                                    >
                                        プレビュー
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* プレビュー画面: 3列×5行グリッド */}
                            {(() => {
                                const ITEMS_PER_PAGE = 15;
                                const totalPages = Math.ceil(preparedMessages.length / ITEMS_PER_PAGE);
                                const startIdx = previewPage * ITEMS_PER_PAGE;
                                const pageItems = preparedMessages.slice(startIdx, startIdx + ITEMS_PER_PAGE);

                                return (
                                    <Box>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: 1.5,
                                            mt: 1,
                                        }}>
                                            {pageItems.map((msg, i) => {
                                                const globalIdx = startIdx + i;
                                                return (
                                                    <Box key={msg.instanceKey} sx={{
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        backgroundColor: '#fafafa',
                                                    }}>
                                                        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, color: '#1976d2' }}>
                                                            {msg.name}
                                                        </Typography>
                                                        <textarea
                                                            value={msg.text}
                                                            onChange={(e) => {
                                                                setPreparedMessages(prev => {
                                                                    const updated = [...prev];
                                                                    updated[globalIdx] = { ...updated[globalIdx], text: e.target.value };
                                                                    return updated;
                                                                });
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                boxSizing: 'border-box',
                                                                minHeight: '120px',
                                                                padding: '6px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #cbd5e1',
                                                                fontSize: '11px',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                lineHeight: 1.4,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {/* ページネーション */}
                                        {totalPages > 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                                                <Button
                                                    size="small"
                                                    disabled={previewPage === 0}
                                                    onClick={() => setPreviewPage(p => p - 1)}
                                                >
                                                    ← 前へ
                                                </Button>
                                                <Typography variant="body2" color="text.secondary">
                                                    {previewPage + 1} / {totalPages} ページ
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    disabled={previewPage >= totalPages - 1}
                                                    onClick={() => setPreviewPage(p => p + 1)}
                                                >
                                                    次へ →
                                                </Button>
                                            </Box>
                                        )}

                                        {/* アクションボタン */}
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                onClick={() => setBroadcastStep('compose')}
                                            >
                                                ← 戻る
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={isSendingBroadcast}
                                                onClick={handleSendBroadcast}
                                            >
                                                {isSendingBroadcast ? '送信中...' : `${preparedMessages.length}件を送信する`}
                                            </Button>
                                        </div>
                                    </Box>
                                );
                            })()}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Chat Dialog */}
            <Dialog
                open={Boolean(selectedInstanceKey)}
                onClose={() => { setSelectedInstanceKey(null); setOpenLineUserId(null); }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: 10500 }}
                PaperProps={{
                    sx: {
                        height: isMobile ? '100%' : '85vh',
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'visible',
                        position: 'relative'
                    }
                }}
            >
                {/* 閉じるボタン（モーダルの外側、または上端に配置） */}
                <IconButton
                    onClick={() => setSelectedInstanceKey(null)}
                    sx={{
                        position: 'absolute',
                        right: isMobile ? 8 : -48,
                        top: isMobile ? 8 : 0,
                        zIndex: 2000,
                        color: isMobile ? '#64748b' : 'white',
                        bgcolor: isMobile ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
                        '&:hover': { 
                            bgcolor: isMobile ? 'white' : 'rgba(0,0,0,0.5)',
                            color: isMobile ? '#000' : 'white'
                        },
                        boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
                    {selectedInstanceKey && (() => {
                        let instance = chatInstances.find(i => i.instanceKey === selectedInstanceKey);
                        if (!instance && !isReservationInstanceKey(selectedInstanceKey)) {
                            // Try to find the same user even if the key changed (e.g. after linking/unlinking)
                            const [lineUserId] = selectedInstanceKey.split('_');
                            instance = chatInstances.find(i => i.line_user_id === lineUserId);
                            
                            // If we found a new instance for the same user, update the key so we don't keep falling back
                            if (instance) {
                                // Use a small timeout to avoid state update during render warning
                                setTimeout(() => setSelectedInstanceKey(instance.instanceKey), 0);
                            }
                        }
                        if (!instance && isReservationInstanceKey(selectedInstanceKey)) {
                            // 一覧に出ていない自社チャット（アーカイブ済みの予約前の会話など）も、取得済みなら開ける
                            const sk = selectedInstanceKey.slice('resv:'.length);
                            const sess = resvChat.sessions.find(x => x.session_key === sk);
                            if (sess) {
                                instance = isReservationSessionKey(sk)
                                    ? toReservationChatInstance(sess, (id) => reservationSyncService.getReservations().find(r => String(r.id) === String(id)), '', '')
                                    : toAnonChatInstance(sess);
                            }
                        }
                        if (!instance) return null;
                        // 同じ予約の LINE と予約チャットは、パネルの上で切り替える（/admin の統合・2026-10-26）
                        const rid = instance.instance_reservation_id != null ? String(instance.instance_reservation_id) : null;
                        const pairLine = chatKindOf(instance) === 'line' ? instance
                            : (rid && instance.kind === 'resv' ? chatInstances.find(i => chatKindOf(i) === 'line' && String(i.instance_reservation_id) === rid) : null);
                        const pairResv = instance.kind === 'resv' ? instance
                            : (rid && chatKindOf(instance) === 'line' ? chatInstances.find(i => i.kind === 'resv' && String(i.instance_reservation_id) === rid) : null);
                        const hasPair = Boolean(pairLine && pairResv);
                        const view = hasPair ? (pairView || (instance.kind === 'resv' ? 'resv' : 'line')) : (isLpChatInstance(instance) ? 'resv' : 'line');
                        const shown = hasPair ? (view === 'resv' ? pairResv : pairLine) : instance;
                        const closePanel = () => { setSelectedInstanceKey(null); setOpenLineUserId(null); };
                        const pairBar = hasPair ? (
                            <div style={{ display: 'flex', gap: 6, padding: '8px 12px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', alignItems: 'center', fontSize: 12 }}>
                                <span style={{ color: '#64748b', fontWeight: 700 }}>同じ予約の会話:</span>
                                {[
                                    { v: 'line', label: 'LINE', unread: (pairLine.unread_count || 0) > 0 },
                                    { v: 'resv', label: '💬予約チャット', unread: (pairResv.unread_count || 0) > 0 },
                                ].map(b => (
                                    <Button key={b.v} size="small" variant={view === b.v ? 'contained' : 'outlined'} onClick={() => setPairView(b.v as 'line' | 'resv')}
                                        sx={{ textTransform: 'none', fontSize: 12, py: 0, borderRadius: '9999px' }}>
                                        {b.label}{b.unread ? ' ●' : ''}
                                    </Button>
                                ))}
                                <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>お客様は両方に書いています。返信はそれぞれのチャネルに届きます</span>
                            </div>
                        ) : null;
                        if (isLpChatInstance(shown)) {
                            const custName = shown.instance_res_data?.customer_name || resvChat.sessions.find(x => x.session_key === shown.session_key)?.resv_customer_name || '';
                            const candidates = shown.kind === 'resv' && lineEnabled && !pairLine
                                ? findLineLinkCandidates(custName, boardUsers, shown.instance_reservation_id, Date.now())
                                : [];
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    {pairBar}
                                    <div style={{ flex: 1, minHeight: 0 }}>
                                        <ReservationChatPanel
                                            instance={shown}
                                            readMode={readMode}
                                            onReload={handleRefresh}
                                            onClose={closePanel}
                                            onOpenReservation={(res) => setSelectedResForEditor(res)}
                                            onOpenSession={(sk) => { setPairView(null); setSelectedInstanceKey(`resv:${sk}`); }}
                                            lineLinkCandidates={candidates}
                                            onLinkLine={shown.instance_reservation_id ? (uid) => linkReservation(uid, String(shown.instance_reservation_id)) : undefined}
                                            fetchTemplates={lineEnabled ? fetchTemplates : undefined}
                                            fetchTemplateCategories={lineEnabled ? fetchTemplateCategories : undefined}
                                        />
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {pairBar}
                                <div style={{ flex: 1, minHeight: 0 }}>
                                    <LineChatPanel
                                        user={shown}
                                        onReload={handleRefresh}
                                        onClose={closePanel}
                                        readMode={readMode}
                                    />
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Notifications */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={4000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity as any} sx={{ width: '100%', borderRadius: '12px', fontWeight: 800 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
            <TemplateHelpDialog 
                open={showHelp} 
                onClose={() => setShowHelp(false)} 
            />
            <BoardHelpDialog
                open={showBoardHelp}
                onClose={() => setShowBoardHelp(false)}
            />

            {/* Staff Registration Dialog */}
            <Dialog open={!!staffRegTarget} onClose={() => setStaffRegTarget(null)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>スタッフチャット登録</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {staffRegTarget?.line_display_name || staffRegTarget?.custom_display_name} をスタッフとして登録します。
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="スタッフ名"
                        value={staffRegName}
                        onChange={(e) => setStaffRegName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && staffRegName.trim()) confirmStaffRegistration(); }}
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setStaffRegTarget(null)} size="small" sx={{ fontWeight: 700 }}>キャンセル</Button>
                        <Button variant="contained" size="small" sx={{ fontWeight: 700 }} disabled={!staffRegName.trim()} onClick={confirmStaffRegistration}>登録</Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Reservation Detail/Editor Panel */}
            {(ReservationEditorPanel as any) && (
            <ReservationEditorPanel
                {...{
                    open: !!selectedResForEditor,
                    editing: editingResForEditor,
                    disabled: isSavingRes,
                    onCancel: () => setSelectedResForEditor(null),
                    onClose: () => setSelectedResForEditor(null),
                    onChange: (updates: any) => setEditingResForEditor(prev => ({ ...prev, ...updates })),
                    onSave: handleSaveReservation,
                    registerNameInputRef: () => {},
                    reservationStatusList: reservationStatusList,
                    vehicles: vehiclesCache,
                    vehicleTypes: vehicleTypeList,
                    onAssignClick: () => {},
                } as any}
            />
            )}

            <style>{`
                .line-detail-btn {
                    padding: 2px 8px;
                    border: 1px solid #3b82f6;
                    color: #2563eb;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-left: auto;
                }
                .line-detail-btn:hover {
                    background: #eff6ff;
                }
                .line-other-res-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: #fef3c7;
                    color: #92400e;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 2px 6px;
                    border-radius: 4px;
                    border: 1px solid #fcd34d;
                    z-index: 10;
                }
                .line-today-tag {
                    font-size: 10px;
                    font-weight: 800;
                    padding: 1px 6px;
                    border-radius: 4px;
                    margin-left: 6px;
                    vertical-align: middle;
                }
                .line-today-tag.pickup {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #10b981;
                }
                .line-today-tag.return {
                    background: #eff6ff;
                    color: #2563eb;
                    border: 1px solid #3b82f6;
                }
                .line-period-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    font-size: 10px;
                    margin-right: 4px;
                    color: white;
                }
                .line-period-icon.pickup { background: #10b981; }
                .line-period-icon.return { background: #3b82f6; }
            `}</style>
        </div>
    );
};
export default LineChatBoard;
